import React, { useMemo, useRef, useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Stack, Typography, Avatar, Chip, TextField, IconButton, InputAdornment,
  Badge, Divider, Tooltip, Menu, MenuItem, Popover, Drawer, alpha, useTheme, useMediaQuery,
  Button, ListItemIcon, Dialog, DialogTitle, DialogContent, DialogActions, Select, FormControl,
  Snackbar, Alert, CircularProgress, Skeleton, Card,
} from '@mui/material';
import {
  Search, Send, Paperclip, Smile, Phone, MoreVertical, Check, CheckCheck,
  ArrowLeft, MessageCircle, Zap, Clock, FileText, ChevronDown, Pencil, Trash2, Ban, Globe, X,
  Bold, Italic, Strikethrough, Image as ImageIcon, Video, File, Download, Plus, Link2,
  CheckCircle2, XCircle, Tag, AlertCircle, RotateCw, Copy, PanelLeftClose, PanelLeftOpen,
  CalendarClock, GitBranch, CornerUpLeft, Eye, ExternalLink, ZoomIn, ZoomOut,
} from 'lucide-react';
import {
  WA_GREEN, WA_TEAL, initialsOf, fmtTime, fmtDay, isWindowOpen, windowTimeLeftLabel, firstUrl, isImageUrl, buildLinkPreview,
  formatSize, EMOJIS, SEED_CONVERSATIONS, SEED_QUICK_REPLIES,
} from './waData';
import { useWaSettings } from './WaSettingsContext';
import { useWaMobile, waDialogProps, waDialogActionsSx, useVisualViewportHeight, useIsPwa } from './WaLayout';
import { waApi } from './waApi';
import { onWaEvent, onWaReconnect, joinWaRoom, leaveWaRoom } from './waSocket';
import { notify } from '../../utils/notificationService';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

// Map a raw backend message doc (from Socket.IO) to the UI message shape.
const normalizeMsg = (m) => {
  let btns = m.buttons || [];
  const dir = m.dir || m.direction;
  const text = m.text || '';

  if ((!btns || btns.length === 0) && dir === 'out' && text) {
    if (text.includes("what best describes you?")) {
      btns = [
        { type: "reply", title: "Seller", url: "" },
        { type: "reply", title: "Wholesaler", url: "" },
        { type: "reply", title: "Manufacturer", url: "" }
      ];
    } else if (text.includes("already selling online") || text.includes("already selling online?")) {
      btns = [
        { type: "reply", title: "Yes", url: "" },
        { type: "reply", title: "No", url: "" }
      ];
    } else if (text.includes("Sellers like you grow even faster on") || text.includes("Sellers like you grow even faster")) {
      btns = [
        { type: "url", title: "Ready for Regeneration?", url: "https://www.resellerbazzar.in/signup" },
        { type: "url", title: "Check Onboarding Process", url: "https://www.resellerbazzar.in/information/onboarding-process" }
      ];
    } else if (text.includes("start selling online from *day one*") || text.includes("start selling online from")) {
      btns = [
        { type: "url", title: "Check Onboarding Process ", url: "https://www.resellerbazzar.in/information/onboarding-process" }
      ];
    } else if (text.includes("When would you like us to call you?") || text.includes("Book a *quick onboarding call")) {
      const slots = [];
      if (/11:00\s*AM|11am/i.test(text)) slots.push("11am");
      if (/4:00\s*PM|4pm/i.test(text)) slots.push("4pm");
      if (slots.length === 0) {
        slots.push("11am", "4pm");
      }
      btns = slots.map((s) => ({ type: "reply", title: s, url: "" }));
    }
  }

  let quoted = m.quoted || m.context || null;
  if (!quoted && m.context_message) {
    quoted = {
      text: m.context_message.text || '',
      author: m.context_message.author || 'Reseller Bazzar',
    };
  }

  return {
    id: String(m._id || m.id),
    waMessageId: m.waMessageId || m.wa_message_id || '',
    clientId: m.clientId || m.client_id || '',
    dir,
    text,
    at: m.at || m.timestamp || m.created_at,
    status: m.status,
    type: m.type || 'text',
    caption: m.caption || '',
    mediaUrl: m.mediaUrl || m.media?.url || '',
    mediaName: m.mediaName || m.media?.filename || '',
    mediaSize: m.mediaSize || m.media?.size || '',
    mediaId: m.mediaId || m.media?.meta_id || m.media_id || '',
    buttons: btns,
    quoted,
    reaction: m.reaction || null,
    location: m.location || null,
    contacts: m.contacts || null,
    errorCode: m.errorCode || m.error_code || '',
    errorReason: m.errorReason || m.error_reason || '',
    edited: m.edited,
    deleted: m.deleted,
  };
};

// Per-send correlation id for an optimistic bubble. Sent to the backend, which
// echoes it back on the saved message so the optimistic bubble can be matched
// and reconciled exactly (no duplicate / stuck bubbles).
const makeClientId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `c-${crypto.randomUUID()}`;
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// Normalize a typed phone number to digits with a country code. A bare 10-digit
// Indian number gets a 91 prefix (mirrors the dialog placeholder 919409343754).
// Returns the normalized number, or '' if it can't be a sensible WhatsApp number.
const normalizePhone = (raw = '') => {
  let digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  // Drop a leading 00 (international dialing prefix).
  if (digits.startsWith('00')) digits = digits.slice(2);
  // Drop leading single 0 (often typed in Indian numbers like 09876543210).
  else if (digits.startsWith('0')) digits = digits.replace(/^0+/, '');
  // Bare 10-digit Indian mobile → prepend 91.
  if (digits.length === 10) digits = `91${digits}`;
  // Sensible E.164-ish range: country code + subscriber number.
  if (digits.length < 10 || digits.length > 15) return '';
  return digits;
};

// Ask once for permission to show desktop notifications for incoming messages.
const ensureNotifyPermission = () => {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission === 'default') Notification.requestPermission().catch(() => {});
};

// Surface an inbound WhatsApp message through the global notifier: always an
// in-app toast, plus a native/OS notification when the tab is in the background
// (routed to /whatsapp by the service worker on click).
const notifyInbound = ({ title, body, tag }) => {
  notify({ title, message: body, severity: 'info', system: true, tag, url: '/whatsapp' });
};

// Parse WhatsApp-style formatting + links into React nodes:
//   *bold*  _italic_  ~strike~  ```mono```  https://links
const RICH_RE = /(```[\s\S]+?```|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|https?:\/\/[^\s]+)/g;
const renderRichText = (text) =>
  String(text).split(RICH_RE).filter((p) => p !== undefined && p !== '').map((p, i) => {
    if (/^https?:\/\//i.test(p)) {
      return <a key={i} href={p} target="_blank" rel="noopener noreferrer" style={{ color: '#027eb5', wordBreak: 'break-all' }}>{p}</a>;
    }
    if (p.startsWith('```') && p.endsWith('```')) {
      return <Box key={i} component="span" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.06)', px: 0.5, borderRadius: 0.5 }}>{p.slice(3, -3)}</Box>;
    }
    if (p.startsWith('*') && p.endsWith('*')) return <strong key={i}>{p.slice(1, -1)}</strong>;
    if (p.startsWith('_') && p.endsWith('_')) return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.startsWith('~') && p.endsWith('~')) return <span key={i} style={{ textDecoration: 'line-through' }}>{p.slice(1, -1)}</span>;
    return p;
  });

// Funnel stages, in order. Keys match the backend waContact.stage enum.
const LIFECYCLE_STAGES = [
  { key: 'open', label: 'Open' },
  { key: 'call_pending', label: 'Call Pending' },
  { key: 'follow_up', label: 'Follow Up' },
];

const StatusTicks = ({ status, readReceipts }) => {
  if (status === 'failed') return <AlertCircle size={14} color="#e3342f" />;
  if (status === 'sent') return <Check size={14} style={{ opacity: 0.7 }} />;
  if (status === 'delivered' || status === 'read') {
    const blue = status === 'read' && readReceipts;
    return <CheckCheck size={15} style={{ color: blue ? '#53bdeb' : undefined, opacity: blue ? 1 : 0.7 }} />;
  }
  // pending / optimistic / missing / unknown status → clock (not yet confirmed sent)
  return <Clock size={13} style={{ opacity: 0.6 }} />;
};

const LinkPreview = ({ preview }) => {
  const theme = useTheme();
  return (
    <Box component="a" href={preview.url} target="_blank" rel="noopener noreferrer"
      sx={{ display: 'flex', gap: 1, p: 1, mb: 0.625, borderRadius: 1.5, textDecoration: 'none',
        bgcolor: alpha(theme.palette.primary.main, 0.06), borderLeft: `3px solid ${theme.palette.primary.main}`,
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}>
      <Box sx={{ width: 42, height: 42, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.12),
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Globe size={18} color={theme.palette.primary.main} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.primary' }}>{preview.title}</Typography>
        <Typography noWrap sx={{ fontSize: 11, color: 'text.secondary' }}>{preview.description}</Typography>
        <Typography noWrap sx={{ fontSize: 10.5, color: 'text.disabled', textTransform: 'uppercase' }}>{preview.domain}</Typography>
      </Box>
    </Box>
  );
};

// A typed link that points straight at an image: try to load and show it
// inline. If the image can't be fetched/decoded, fall back to the link card so
// the message never ends up with a broken-image placeholder.
const ImageLinkPreview = ({ url }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    const preview = buildLinkPreview(url);
    return preview ? <LinkPreview preview={preview} /> : null;
  }
  return (
    <Box component="a" href={url} target="_blank" rel="noopener noreferrer"
      sx={{ display: 'block', mb: 0.625, lineHeight: 0 }}>
      <Box component="img" src={url} alt="" loading="lazy" onError={() => setFailed(true)}
        sx={{ maxWidth: 240, maxHeight: 260, borderRadius: 1.5, display: 'block', objectFit: 'cover' }} />
    </Box>
  );
};

// Resolve the best src for a media message: a stored URL if present, otherwise
// lazy-load the bytes through the authenticated proxy (inbound media) and hand
// back an object URL, revoked on unmount.
const useMediaSrc = (msg) => {
  const [src, setSrc] = useState(msg.mediaUrl || '');
  const [loading, setLoading] = useState(!msg.mediaUrl && !!msg.mediaId);
  useEffect(() => {
    if (msg.mediaUrl) { setSrc(msg.mediaUrl); setLoading(false); return undefined; }
    if (!msg.mediaId) { setLoading(false); return undefined; }
    let active = true;
    let objectUrl = '';
    setLoading(true);
    waApi.getMessageMedia(msg.mediaId)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
        setLoading(false);
      })
      .catch(() => {
        if (active) setLoading(false);
        /* leave placeholder on failure */
      });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [msg.mediaUrl, msg.mediaId]);
  return { src, loading };
};

// Cross-origin safe file download helper
const triggerMediaDownload = async (url, filename = 'download') => {
  if (!url) return;
  try {
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    const res = await fetch(url);
    if (!res.ok) throw new Error('Fetch failed');
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
  } catch {
    // Fallback: direct anchor download or window.open
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
};

// Fullscreen / Lightbox Media Preview Dialog
const MediaPreviewModal = ({ open, onClose, mediaSrc, type = 'image', name = '', size = '', caption = '' }) => {
  const theme = useTheme();
  const [zoom, setZoom] = useState(1);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open) {
      setZoom(1);
    }
  }, [open]);

  if (!open) return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const ext = type === 'video' ? 'mp4' : type === 'audio' ? 'mp3' : type === 'document' ? 'pdf' : 'jpg';
      const filename = name || `whatsapp_${type}_${Date.now()}.${ext}`;
      await triggerMediaDownload(mediaSrc, filename);
    } finally {
      setDownloading(false);
    }
  };

  const isDoc = type === 'document' || type === 'file';
  const isPdf = isDoc && (name?.toLowerCase().endsWith('.pdf') || mediaSrc?.toLowerCase().includes('.pdf'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.mode === 'dark' ? '#0b141a' : '#111b21',
          color: '#ffffff',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
        },
      }}
    >
      {/* Top Header Bar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: alpha('#000000', 0.4),
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1, mr: 2 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              bgcolor: alpha(WA_TEAL, 0.2),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {type === 'video' ? (
              <Video size={18} color={WA_TEAL} />
            ) : isDoc ? (
              <File size={18} color={theme.palette.error.main} />
            ) : (
              <ImageIcon size={18} color={WA_TEAL} />
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: '#ffffff', fontSize: 13.5 }}>
              {name || (type === 'video' ? 'WhatsApp Video' : isDoc ? 'Document' : 'WhatsApp Image')}
            </Typography>
            {size && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>
                {size}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          {type === 'image' && (
            <>
              <Tooltip title="Zoom Out">
                <IconButton
                  size="small"
                  onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                  sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#ffffff' } }}
                >
                  <ZoomOut size={17} />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', minWidth: 42, textAlign: 'center', fontSize: 11 }}>
                {Math.round(zoom * 100)}%
              </Typography>
              <Tooltip title="Zoom In">
                <IconButton
                  size="small"
                  onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
                  sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#ffffff' } }}
                >
                  <ZoomIn size={17} />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.15)', mx: 0.5, my: 0.5 }} />
            </>
          )}

          <Tooltip title="Download File">
            <Button
              size="small"
              variant="contained"
              onClick={handleDownload}
              disabled={downloading || !mediaSrc}
              startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <Download size={15} />}
              sx={{
                bgcolor: WA_TEAL,
                color: '#fff',
                fontWeight: 700,
                fontSize: 12,
                borderRadius: 2,
                px: 1.5,
                '&:hover': { bgcolor: '#008f72' },
              }}
            >
              {downloading ? 'Downloading...' : 'Download'}
            </Button>
          </Tooltip>

          <Tooltip title="Open in New Tab">
            <IconButton
              size="small"
              component="a"
              href={mediaSrc}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#ffffff' } }}
            >
              <ExternalLink size={17} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Close Preview">
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              <X size={19} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Main Preview Container */}
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: 280, sm: 400 },
          maxHeight: '75vh',
          overflow: 'auto',
          bgcolor: '#090e11',
          position: 'relative',
        }}
      >
        {type === 'video' ? (
          <Box
            component="video"
            src={mediaSrc}
            controls
            autoPlay
            sx={{
              maxWidth: '100%',
              maxHeight: '72vh',
              display: 'block',
              outline: 'none',
              borderRadius: 1,
            }}
          />
        ) : isPdf ? (
          <Box
            component="iframe"
            src={mediaSrc}
            title={name || 'PDF Preview'}
            sx={{
              width: '100%',
              height: '70vh',
              border: 'none',
              bgcolor: '#ffffff',
            }}
          />
        ) : isDoc ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.error.main, 0.15),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <File size={36} color={theme.palette.error.main} />
            </Box>
            <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 0.5 }}>
              {name || 'Document'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
              {size ? `${size} • ` : ''}Preview is available via direct download or in a new browser tab.
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center">
              <Button
                variant="contained"
                onClick={handleDownload}
                startIcon={<Download size={16} />}
                sx={{ bgcolor: WA_TEAL, '&:hover': { bgcolor: '#008f72' }, fontWeight: 700 }}
              >
                Download File
              </Button>
              <Button
                variant="outlined"
                component="a"
                href={mediaSrc}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<ExternalLink size={16} />}
                sx={{ color: '#ffffff', borderColor: 'rgba(255,255,255,0.3)', fontWeight: 600 }}
              >
                Open in Tab
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              overflow: 'auto',
              p: 2,
            }}
          >
            <Box
              component="img"
              src={mediaSrc}
              alt={name || 'Preview'}
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 1,
                transform: `scale(${zoom})`,
                transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                transformOrigin: 'center center',
                boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
              }}
            />
          </Box>
        )}
      </DialogContent>

      {/* Caption footer if present */}
      {caption && (
        <Box sx={{ px: 3, py: 1.5, bgcolor: alpha('#000000', 0.5), borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Typography sx={{ fontSize: 13.5, color: '#e9edef', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45 }}>
            {renderRichText(caption)}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

// Image/video/file message body with interactive preview & download.
const MediaBody = ({ msg }) => {
  const theme = useTheme();
  const { src: mediaSrc, loading: fetching } = useMediaSrc(msg);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Reset loaded state whenever src changes so spinner shows until new image loads
  const prevSrcRef = React.useRef(mediaSrc);
  if (prevSrcRef.current !== mediaSrc) {
    prevSrcRef.current = mediaSrc;
    if (mediaSrc) setMediaLoaded(false);
  }

  const showLoader = fetching || (!!mediaSrc && !mediaLoaded);

  const handleDownload = async (e) => {
    if (e) e.stopPropagation();
    if (!mediaSrc) return;
    setDownloading(true);
    try {
      const ext = msg.type === 'video' ? 'mp4' : msg.type === 'audio' ? 'mp3' : msg.type === 'document' ? 'pdf' : 'jpg';
      const filename = msg.mediaName || `whatsapp_${msg.type}_${Date.now()}.${ext}`;
      await triggerMediaDownload(mediaSrc, filename);
    } finally {
      setDownloading(false);
    }
  };

  // audio → HTML5 player + download
  if (msg.type === 'audio') {
    return (
      <Box sx={{ minWidth: 200 }}>
        {fetching && !mediaSrc ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
            <CircularProgress size={18} color="primary" />
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Loading audio…</Typography>
          </Stack>
        ) : mediaSrc ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box component="audio" src={mediaSrc} controls sx={{ width: 220, maxWidth: '100%', display: 'block' }} />
            <Tooltip title="Download Audio">
              <IconButton size="small" onClick={handleDownload} disabled={downloading} sx={{ color: 'text.secondary' }}>
                {downloading ? <CircularProgress size={16} color="inherit" /> : <Download size={16} />}
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Audio unavailable</Typography>
        )}
        {msg.caption && (
          <Typography sx={{ fontSize: 14, lineHeight: 1.4, mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {renderRichText(msg.caption)}
          </Typography>
        )}
      </Box>
    );
  }

  // image / video / sticker render interactive media view with lightbox & download
  if (msg.type === 'image' || msg.type === 'video' || msg.type === 'sticker') {
    return (
      <>
        <Box
          onClick={() => mediaSrc && setPreviewOpen(true)}
          sx={{
            position: 'relative',
            display: 'inline-block',
            maxWidth: 240,
            minWidth: 150,
            minHeight: 140,
            cursor: mediaSrc ? 'pointer' : 'default',
            borderRadius: 1.5,
            overflow: 'hidden',
            '&:hover .wa-media-overlay': { opacity: 1 },
          }}
        >
          {showLoader && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.7) : 'rgba(255, 255, 255, 0.7)',
              borderRadius: 1.5,
              zIndex: 10,
              minHeight: 140,
            }}>
              <CircularProgress size={28} color="primary" />
            </Box>
          )}

          {/* Action Overlay: Preview & Download */}
          {mediaSrc && (
            <Box
              className="wa-media-overlay"
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                display: 'flex',
                gap: 0.5,
                zIndex: 12,
                opacity: { xs: 1, sm: 0 },
                transition: 'opacity 0.2s ease',
                bgcolor: 'rgba(0, 0, 0, 0.65)',
                borderRadius: 2,
                p: '2px 4px',
                backdropFilter: 'blur(4px)',
              }}
            >
              <Tooltip title="Preview Full Size">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewOpen(true);
                  }}
                  sx={{ color: '#ffffff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                >
                  <Eye size={15} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  disabled={downloading}
                  sx={{ color: '#ffffff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                >
                  {downloading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Download size={15} />}
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {mediaSrc ? (
            msg.type === 'video' ? (
              <Box
                component="video"
                src={mediaSrc}
                controls
                onLoadedData={() => setMediaLoaded(true)}
                onError={() => setMediaLoaded(true)}
                sx={{
                  maxWidth: 240,
                  maxHeight: 320,
                  borderRadius: 1.5,
                  display: 'block',
                  visibility: mediaLoaded ? 'visible' : 'hidden',
                }}
              />
            ) : (
              <Box
                component="img"
                src={mediaSrc}
                alt={msg.mediaName}
                onLoad={() => setMediaLoaded(true)}
                onError={() => setMediaLoaded(true)}
                sx={{
                  maxWidth: 240,
                  maxHeight: 260,
                  borderRadius: 1.5,
                  display: 'block',
                  objectFit: 'cover',
                  visibility: mediaLoaded ? 'visible' : 'hidden',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.01)' },
                }}
              />
            )
          ) : (
            !fetching && (
              <Box sx={{
                width: 220,
                height: 140,
                borderRadius: 1.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                background: `linear-gradient(135deg, ${alpha(WA_TEAL, 0.18)}, ${alpha(WA_GREEN, 0.12)})`
              }}>
                {msg.type === 'video' ? <Video size={30} color={WA_TEAL} /> : <ImageIcon size={30} color={WA_TEAL} />}
                <Typography sx={{ fontSize: 11, color: 'text.secondary', px: 1, textAlign: 'center' }} noWrap>{msg.mediaName}</Typography>
              </Box>
            )
          )}

          {msg.caption && (
            <Typography sx={{ fontSize: 14, lineHeight: 1.4, mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {renderRichText(msg.caption)}
            </Typography>
          )}
        </Box>

        <MediaPreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          mediaSrc={mediaSrc}
          type={msg.type}
          name={msg.mediaName}
          size={msg.mediaSize}
          caption={msg.caption}
        />
      </>
    );
  }

  // file / document with preview & download actions
  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        onClick={() => mediaSrc && setPreviewOpen(true)}
        sx={{
          p: 1,
          borderRadius: 1.5,
          bgcolor: alpha(theme.palette.grey[500], 0.1),
          minWidth: 210,
          cursor: mediaSrc ? 'pointer' : 'default',
          transition: 'all 0.2s',
          '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.16) },
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.error.main, 0.12),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <File size={18} color={theme.palette.error.main} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>{msg.mediaName || 'Document'}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{msg.mediaSize || ''}</Typography>
        </Box>
        <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Preview Document">
            <IconButton
              size="small"
              onClick={() => setPreviewOpen(true)}
              disabled={!mediaSrc}
              aria-label="Preview attachment"
              sx={{ color: 'text.secondary' }}
            >
              <Eye size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download Document">
            <IconButton
              size="small"
              onClick={handleDownload}
              disabled={!mediaSrc || downloading}
              aria-label="Download attachment"
              sx={{ color: 'text.secondary' }}
            >
              {downloading ? <CircularProgress size={16} color="inherit" /> : <Download size={16} />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <MediaPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        mediaSrc={mediaSrc}
        type="document"
        name={msg.mediaName}
        size={msg.mediaSize}
        caption={msg.caption}
      />
    </>
  );
};

// Template header image preview & download
const TemplateMediaPreview = ({ mediaUrl, title }) => {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          mb: 0.75,
          lineHeight: 0,
          position: 'relative',
          display: 'inline-block',
          cursor: 'pointer',
          borderRadius: 1.5,
          overflow: 'hidden',
          '&:hover .wa-media-overlay': { opacity: 1 },
        }}
      >
        <Box
          component="img"
          src={mediaUrl}
          alt={title || ''}
          loading="lazy"
          sx={{ maxWidth: { xs: 220, sm: 240 }, maxHeight: 260, borderRadius: 1.5, display: 'block', objectFit: 'cover' }}
        />
        <Box
          className="wa-media-overlay"
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            display: 'flex',
            gap: 0.5,
            zIndex: 12,
            opacity: { xs: 1, sm: 0 },
            transition: 'opacity 0.2s ease',
            bgcolor: 'rgba(0, 0, 0, 0.65)',
            borderRadius: 2,
            p: '2px 4px',
            backdropFilter: 'blur(4px)',
          }}
        >
          <Tooltip title="Preview">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setOpen(true); }}
              sx={{ color: '#ffffff', p: 0.5 }}
            >
              <Eye size={14} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={async (e) => {
                e.stopPropagation();
                setDownloading(true);
                try {
                  await triggerMediaDownload(mediaUrl, 'template_header.jpg');
                } finally {
                  setDownloading(false);
                }
              }}
              disabled={downloading}
              sx={{ color: '#ffffff', p: 0.5 }}
            >
              {downloading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Download size={14} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <MediaPreviewModal
        open={open}
        onClose={() => setOpen(false)}
        mediaSrc={mediaUrl}
        type="image"
        name={title || 'Template Image'}
      />
    </>
  );
};

const MessageBubbleBase = ({ msg, onEdit, onDelete, onRetry }) => {
  const theme = useTheme();
  const { readReceipts } = useWaSettings();
  const out = msg.dir === 'out';
  const [anchor, setAnchor] = useState(null);
  const isMedia = msg.type === 'image' || msg.type === 'video' || msg.type === 'sticker'
    || msg.type === 'audio' || msg.type === 'document' || msg.type === 'file';
  const isReaction = msg.type === 'reaction';
  const isLocation = msg.type === 'location';
  const isContacts = msg.type === 'contacts';
  const isSpecial = isReaction || isLocation || isContacts;
  const url = (!msg.deleted && !isMedia && !isSpecial) ? firstUrl(msg.text) : null;
  const preview = url ? buildLinkPreview(url) : null;

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: out ? 'flex-end' : 'flex-start',
      px: { xs: 1, md: 2.5 },
      mb: 1,
      '&:hover .wa-msg-actions': { opacity: 1 },
      // Touch devices have no hover, so the edit/delete trigger would be
      // unreachable — keep it visible there.
      '@media (hover: none)': { '& .wa-msg-actions': { opacity: 1 } },
    }}>
      <Box
        sx={{
          position: 'relative',
          maxWidth: { xs: '88%', sm: '78%', md: '68%' },
          px: { xs: 1.25, sm: 1.75 },
          py: 0.85,
          borderRadius: '8px',
          borderTopRightRadius: out ? '0px' : '8px', 
          borderTopLeftRadius: out ? '8px' : '0px',
          bgcolor: msg.deleted
            ? alpha(theme.palette.grey[500], 0.1)
            : out
              ? (theme.palette.mode === 'dark' ? '#005c4b' : '#d9fdd3')
              : (theme.palette.mode === 'dark' ? '#202c33' : '#ffffff'),
          color: theme.palette.mode === 'dark' ? '#e9edef' : '#111b21',
          border: `1px solid ${theme.palette.mode === 'dark' 
            ? (out ? alpha('#00a884', 0.15) : alpha(theme.palette.divider, 0.15)) 
            : (out ? alpha('#00a884', 0.2) : alpha(theme.palette.divider, 0.4))}`, 
          boxShadow: theme.palette.mode === 'dark'
            ? '0 1px 2px rgba(0, 0, 0, 0.3)'
            : '0 1px 2px rgba(11, 20, 26, 0.12)',
          transition: 'all 0.2s ease',
        }}
      >
        {!msg.deleted && (
          <IconButton 
            size="small" 
            aria-label="Message actions" 
            className="wa-msg-actions" 
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{ 
              position: 'absolute', 
              top: 4, 
              right: 4, 
              p: 0.25, 
              opacity: 0, 
              transition: 'all .15s ease', 
              zIndex: 1,
              color: 'text.secondary',
              bgcolor: alpha(theme.palette.background.paper, 0.9), 
              '&:hover': { bgcolor: theme.palette.background.paper } 
            }}
          >
            <ChevronDown size={13} />
          </IconButton>
        )}

        {msg.deleted ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 0.5 }}>
            <Ban size={13} style={{ opacity: 0.55 }} />
            <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: 'text.disabled', fontFamily: 'Inter, sans-serif' }}>
              {out ? 'You deleted this message' : 'This message was deleted'}
            </Typography>
          </Stack>
        ) : isReaction ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 1.5 }}>
            <Typography component="span" sx={{ fontSize: 26, lineHeight: 1 }}>
              {msg.reaction?.emoji || msg.text || '👍'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
              {out ? 'You reacted' : 'Reacted'}
            </Typography>
          </Stack>
        ) : isLocation ? (
          <Box sx={{ minWidth: 200, pr: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Typography component="span" sx={{ fontSize: 22, lineHeight: 1 }}>📍</Typography>
              <Box sx={{ minWidth: 0 }}>
                {msg.location?.name && (
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, fontFamily: 'Inter, sans-serif', wordBreak: 'break-word' }}>
                    {msg.location.name}
                  </Typography>
                )}
                {msg.location?.address && (
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontFamily: 'Inter, sans-serif', wordBreak: 'break-word' }}>
                    {msg.location.address}
                  </Typography>
                )}
                {(msg.location?.latitude != null && msg.location?.longitude != null) && (
                  <a
                    href={`https://maps.google.com/?q=${msg.location.latitude},${msg.location.longitude}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: '#027eb5', fontSize: 12.5, fontWeight: 700, textDecoration: 'none' }}>
                    Open in Maps
                  </a>
                )}
              </Box>
            </Stack>
          </Box>
        ) : isContacts ? (
          <Box sx={{ minWidth: 200, pr: 1.5 }}>
            <Stack spacing={1}>
              {(msg.contacts || []).map((ct, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  <Typography component="span" sx={{ fontSize: 20, lineHeight: 1 }}>👤</Typography>
                  <Box sx={{ minWidth: 0 }}>
                    {ct.name && (
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, fontFamily: 'Inter, sans-serif', wordBreak: 'break-word' }}>
                        {ct.name}
                      </Typography>
                    )}
                    {ct.phone && (
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontFamily: 'Inter, sans-serif', wordBreak: 'break-word' }}>
                        {ct.phone}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>
        ) : isMedia ? (
          <MediaBody msg={msg} />
        ) : (
          <>
            {msg.quoted && (
              <Box sx={{
                mb: 1,
                p: 1,
                borderRadius: '6px',
                bgcolor: theme.palette.mode === 'dark' ? '#182229' : '#f0f2f5',
                borderLeft: '4px solid #ff2e93',
                minWidth: 180,
              }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#ff2e93', fontFamily: 'Inter, sans-serif' }}>
                  {msg.quoted.author || 'Reseller Bazzar'}
                </Typography>
                <Typography noWrap sx={{ fontSize: 12, color: theme.palette.mode === 'dark' ? '#8696a0' : '#54656f', fontFamily: 'Inter, sans-serif' }}>
                  {msg.quoted.text}
                </Typography>
              </Box>
            )}
            {msg.type === 'template' && msg.mediaUrl && (
              <TemplateMediaPreview mediaUrl={msg.mediaUrl} title={msg.text?.slice(0, 30)} />
            )}
            {url && (isImageUrl(url) ? <ImageLinkPreview url={url} /> : preview && <LinkPreview preview={preview} />)}
            <Typography sx={{
              fontSize: 14, 
              lineHeight: 1.45, 
              color: theme.palette.mode === 'dark' ? '#e9edef' : '#111b21', 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              boxSizing: 'border-box',
              fontFamily: 'Inter, sans-serif',
              pr: 2 
            }}>
              {renderRichText(msg.text)}
            </Typography>

            {msg.buttons && msg.buttons.length > 0 && (
              <Box sx={{ 
                mt: 1,
                pt: 0.5,
                borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                display: 'flex', 
                flexDirection: 'column', 
                gap: 0.75, 
                width: '100%' 
              }}>
                {msg.buttons.map((btn, idx) => {
                  if (btn.type === 'url') {
                    return (
                      <Button
                        key={idx}
                        variant="text"
                        size="small"
                        href={btn.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<Link2 size={15} style={{ color: '#00a884' }} />}
                        sx={{
                          textTransform: 'none',
                          fontSize: 13,
                          fontWeight: 600,
                          borderRadius: '8px',
                          width: '100%',
                          justifyContent: 'center',
                          py: 0.75,
                          px: 1.5,
                          bgcolor: theme.palette.mode === 'dark' ? '#233138' : '#ffffff',
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                          color: '#00a884',
                          '&:hover': {
                            bgcolor: theme.palette.mode === 'dark' ? '#2a3942' : '#f0f2f5',
                          }
                        }}
                      >
                        {btn.title}
                      </Button>
                    );
                  }
                  return (
                    <Button
                      key={idx}
                      variant="text"
                      size="small"
                      startIcon={<CornerUpLeft size={15} style={{ color: '#00a884' }} />}
                      sx={{
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 600,
                        borderRadius: '8px',
                        width: '100%',
                        justifyContent: 'center',
                        py: 0.75,
                        px: 1.5,
                        bgcolor: theme.palette.mode === 'dark' ? '#233138' : '#ffffff',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                        color: '#00a884',
                        fontFamily: 'Inter, sans-serif',
                        '&.Mui-disabled': {
                          color: '#00a884',
                        }
                      }}
                    >
                      {btn.title}
                    </Button>
                  );
                })}
              </Box>
            )}
          </>
        )}

        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ mt: 0.25, gap: 0.25 }}>
          {msg.edited && !msg.deleted && (
            <Typography sx={{ fontSize: 10.5, color: out ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(11,20,26,0.45)') : 'text.secondary', fontStyle: 'italic', fontFamily: 'Inter, sans-serif' }}>edited</Typography>
          )}
          <Typography sx={{ fontSize: 10.5, fontWeight: 500, color: out ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(11,20,26,0.5)') : 'text.secondary', fontFamily: 'Inter, sans-serif' }}>{fmtTime(msg.at)}</Typography>
          {out && !msg.deleted && <StatusTicks status={msg.status} readReceipts={readReceipts} />}
        </Stack>

        {out && !msg.deleted && msg.status === 'failed' && (
          <Box sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={0.5} alignItems="center" onClick={() => onRetry?.(msg)}
              sx={{ cursor: 'pointer', color: '#e3342f', '&:hover': { textDecoration: 'underline' } }}>
              <RotateCw size={12} />
              <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                Delivery Failed {msg.errorCode ? `(#${msg.errorCode})` : ''} — tap to retry
              </Typography>
            </Stack>
            {msg.errorReason && (
              <Typography sx={{ fontSize: 9.5, color: '#c53030', mt: 0.25, fontStyle: 'italic', maxWidth: 280, wordBreak: 'break-word', lineHeight: 1.25 }}>
                {msg.errorReason}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: out ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: out ? 'right' : 'left' }}>
        {out && !isMedia && (
          <MenuItem onClick={() => { onEdit(msg); setAnchor(null); }} sx={{ fontSize: 13, fontFamily: 'Inter, sans-serif' }}>
            <Pencil size={14} style={{ marginRight: 8 }} />Edit
          </MenuItem>
        )}
        <MenuItem onClick={() => { onDelete(msg); setAnchor(null); }} sx={{ fontSize: 13, color: 'error.main', fontFamily: 'Inter, sans-serif' }}>
          <Trash2 size={14} style={{ marginRight: 8 }} />Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

// Memoize the bubble: re-render only when a meaningful field of THIS message
// changes (id/text/status/edited/deleted/type/caption/media/buttons). The
// callbacks (onEdit/onDelete/onRetry) only act on the `msg` they're given (or
// the open chat's stable id), so their identity is intentionally not compared —
// otherwise the parent re-creating them on every render would defeat the memo.
const MessageBubble = React.memo(MessageBubbleBase, (prev, next) => {
  const a = prev.msg;
  const b = next.msg;
  return (
    a.id === b.id &&
    a.text === b.text &&
    a.status === b.status &&
    a.edited === b.edited &&
    a.deleted === b.deleted &&
    a.type === b.type &&
    a.caption === b.caption &&
    a.mediaUrl === b.mediaUrl &&
    a.mediaId === b.mediaId &&
    a.buttons === b.buttons &&
    a.reaction === b.reaction &&
    a.location === b.location &&
    a.contacts === b.contacts
  );
});

const ConversationRowBase = ({ conv, active, onClick, onMarkRead }) => {
  const theme = useTheme();
  const windowOpen = isWindowOpen(conv.lastInboundAt);
  return (
    <Box
      className="conv-row"
      onClick={onClick}
      sx={{
        display: 'flex', 
        alignItems: 'center', 
        gap: { xs: 1.25, sm: 1.5 }, 
        px: { xs: 1.5, sm: 2 }, 
        py: { xs: 1.25, sm: 1.5 }, 
        cursor: 'pointer',
        borderLeft: `4px solid ${active ? WA_TEAL : 'transparent'}`,
        bgcolor: active 
          ? (theme.palette.mode === 'dark' ? alpha(WA_TEAL, 0.15) : alpha(WA_TEAL, 0.06))
          : 'transparent',
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.divider, 0.65)}`,
        '&:hover': { 
          bgcolor: active 
            ? (theme.palette.mode === 'dark' ? alpha(WA_TEAL, 0.18) : alpha(WA_TEAL, 0.08))
            : alpha(theme.palette.action.hover, 0.6),
          '& .conv-avatar': { transform: 'scale(1.03)' }
        },
        '&:active': {
          bgcolor: alpha(WA_TEAL, 0.12),
        },
        transition: 'all 0.2s ease',
      }}
    >
      <Badge 
        color="success" 
        variant="dot" 
        overlap="circular" 
        invisible={!windowOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{
          '& .MuiBadge-badge': {
            boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
            transform: 'scale(1.1) translate(2px, 2px)',
          }
        }}
      >
        <Avatar 
          className="conv-avatar"
          sx={{ 
            width: { xs: 40, sm: 44 }, 
            height: { xs: 40, sm: 44 }, 
            bgcolor: active ? WA_TEAL : alpha(WA_TEAL, 0.85), 
            color: '#fff', 
            fontWeight: 800, 
            fontSize: { xs: 13, sm: 14 },
            fontFamily: 'Outfit, sans-serif',
            boxShadow: active ? `0 4px 12px ${alpha(WA_TEAL, 0.25)}` : 'none',
            transition: 'transform 0.2s ease'
          }}
        >
          {initialsOf(conv.name)}
        </Avatar>
      </Badge>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography 
            noWrap 
            sx={{ 
              fontSize: 13.5, 
              fontWeight: conv.unread ? 800 : 700, 
              fontFamily: 'Outfit, sans-serif',
              color: 'text.primary'
            }}
          >
            {conv.name}
          </Typography>
          <Typography 
            sx={{ 
              fontSize: 10.5, 
              color: conv.unread ? WA_TEAL : 'text.disabled', 
              fontWeight: conv.unread ? 800 : 500, 
              flexShrink: 0, 
              ml: 1,
              fontFamily: 'Inter, sans-serif'
            }}
          >
            {fmtDay(conv.lastInboundAt)}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
          <Typography 
            noWrap 
            sx={{ 
              fontSize: 12.5, 
              color: conv.unread ? 'text.primary' : 'text.secondary', 
              fontWeight: conv.unread ? 600 : 400,
              fontFamily: 'Inter, sans-serif',
              flex: 1, 
              minWidth: 0 
            }}
          >
            {conv.lastMessage}
          </Typography>
          {conv.unread > 0 && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 1, flexShrink: 0 }}>
              <Tooltip title="Mark as read">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onMarkRead) onMarkRead(conv.id, conv.name);
                  }}
                  sx={{
                    width: 22,
                    height: 22,
                    p: 0,
                    color: 'text.secondary',
                    opacity: 0,
                    '.conv-row:hover &': { opacity: 1 },
                    '&:hover': { color: WA_TEAL },
                    transition: 'opacity 0.2s, color 0.2s',
                  }}
                >
                  <CheckCheck size={14} />
                </IconButton>
              </Tooltip>
              <Box sx={{ 
                minWidth: 18, 
                height: 18, 
                px: 0.5, 
                borderRadius: 9, 
                bgcolor: WA_TEAL, 
                color: '#fff',
                fontSize: 10, 
                fontWeight: 800, 
                fontFamily: 'Inter, sans-serif',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0,
                boxShadow: `0 2px 6px ${alpha(WA_TEAL, 0.3)}`
              }}>
                {conv.unread}
              </Box>
            </Stack>
          )}
        </Stack>
        {conv.tags?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            {conv.tags.slice(0, 2).map((t) => (
              <Chip 
                key={t} 
                label={t} 
                size="small" 
                sx={{ 
                  height: 20,
                  fontSize: 10.5,
                  fontWeight: 700,
                  fontFamily: 'Inter, sans-serif',
                  bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.grey[500], 0.08),
                  color: 'text.secondary',
                  borderRadius: 1
                }} 
              />
            ))}
            {conv.tags.length > 2 && (
              <Chip 
                label={`+${conv.tags.length - 2}`} 
                size="small" 
                sx={{ 
                  height: 20,
                  fontSize: 10.5,
                  fontWeight: 700,
                  fontFamily: 'Inter, sans-serif',
                  bgcolor: 'transparent',
                  color: 'text.disabled',
                  border: `1px dashed ${theme.palette.divider}`,
                  borderRadius: 1
                }} 
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Memoize the list row: re-render only when a field this row actually shows
// changes, or when its selected state flips. `onClick` is recreated inline by
// the parent on every render but only ever calls openConversation(conv.id)
// (which reads live state via refs), so its identity is deliberately ignored.
const ConversationRow = React.memo(ConversationRowBase, (prev, next) => {
  const a = prev.conv;
  const b = next.conv;
  return (
    prev.active === next.active &&
    a.id === b.id &&
    a.name === b.name &&
    a.lastMessage === b.lastMessage &&
    a.lastInboundAt === b.lastInboundAt &&
    a.unread === b.unread &&
    a.tags === b.tags
  );
});

// Small uppercase section heading used inside the profile drawer.
const DrawerSectionTitle = ({ icon: Icon, children, action }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {Icon && <Icon size={14} color={theme.palette.text.secondary} />}
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>{children}</Typography>
      </Stack>
      {action}
    </Stack>
  );
};

// A label/value row in the Details section.
const DrawerDetailRow = ({ icon: Icon, label, children }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary', minWidth: 0 }}>
      <Icon size={14} />
      <Typography sx={{ fontSize: 13 }}>{label}</Typography>
    </Stack>
    <Box sx={{ minWidth: 0, textAlign: 'right' }}>{children}</Box>
  </Stack>
);

// Right-hand contact profile with quick actions, funnel-stage + tag management.
const ProfileDrawer = ({ open, onClose, conv, onAddTag, onRemoveTag, allTags = [], onSetStage, onSendCallSchedule }) => {
  const theme = useTheme();
  const [tagAnchor, setTagAnchor] = useState(null);
  const [copied, setCopied] = useState(false);
  if (!conv) return null;
  const tagColor = (name) => allTags.find((t) => t.name === name)?.color || theme.palette.grey[500];
  const available = allTags.filter((t) => !conv.tags.includes(t.name));
  const windowOpen = isWindowOpen(conv.lastInboundAt);
  const timeLeftLabel = windowTimeLeftLabel(conv.lastInboundAt);
  const stageLabel = LIFECYCLE_STAGES.find((s) => s.key === conv.stage)?.label;

  const copyNumber = () => {
    if (!conv.phone) return;
    navigator.clipboard?.writeText(conv.phone)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })
      .catch(() => {});
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 360, md: 380 } } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Hero header */}
        <Box sx={{ position: 'relative', px: 2, pt: 3, pb: 2.25, textAlign: 'center', flexShrink: 0,
          background: `linear-gradient(160deg, ${alpha(WA_TEAL, 0.16)}, ${alpha(WA_GREEN, 0.05)})`,
          borderBottom: `1px solid ${theme.palette.divider}` }}>
          <IconButton size="small" onClick={onClose} aria-label="Close contact info"
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: alpha(theme.palette.background.paper, 0.7),
              '&:hover': { bgcolor: theme.palette.background.paper } }}>
            <X size={18} />
          </IconButton>
          <Avatar sx={{ width: { xs: 72, sm: 88 }, height: { xs: 72, sm: 88 }, mx: 'auto', mb: 1.25, bgcolor: alpha(WA_TEAL, 0.9), color: '#fff',
            fontWeight: 800, fontSize: { xs: 26, sm: 32 }, boxShadow: theme.shadows[3] }}>
            {initialsOf(conv.name)}
          </Avatar>
          <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2, px: 3 }} noWrap>{conv.name}</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>{conv.phone}</Typography>
          {stageLabel && (
            <Chip size="small" label={stageLabel} color="primary" variant="outlined" sx={{ mt: 1, fontWeight: 700, fontSize: 11 }} />
          )}
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1.75 }}>
            <Tooltip title="Call (opens dialer)">
              <IconButton component="a" href={`tel:${conv.phone}`} aria-label="Call contact"
                sx={{ bgcolor: theme.palette.background.paper, boxShadow: theme.shadows[1], '&:hover': { bgcolor: alpha(WA_GREEN, 0.12) } }}>
                <Phone size={18} color={WA_TEAL} />
              </IconButton>
            </Tooltip>
            <Tooltip title={copied ? 'Copied!' : 'Copy number'}>
              <IconButton onClick={copyNumber} aria-label="Copy number"
                sx={{ bgcolor: theme.palette.background.paper, boxShadow: theme.shadows[1] }}>
                {copied ? <Check size={18} color={theme.palette.success.main} /> : <Copy size={18} />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {/* Funnel stage */}
          <Box sx={{ p: 2 }}>
            <DrawerSectionTitle icon={GitBranch}>Funnel stage</DrawerSectionTitle>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {LIFECYCLE_STAGES.map((s) => (
                <Chip key={s.key} label={s.label} size="small" clickable
                  onClick={() => onSetStage?.(s.key)}
                  color={conv.stage === s.key ? 'primary' : 'default'}
                  variant={conv.stage === s.key ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 700, fontSize: 11 }} />
              ))}
            </Box>
            {onSendCallSchedule && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  fullWidth
                  startIcon={<CalendarClock size={14} />}
                  onClick={onSendCallSchedule}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, borderRadius: 2 }}
                >
                  Send Call Schedule Prompt
                </Button>
              </Box>
            )}
          </Box>
          <Divider />

          {/* Tags */}
          <Box sx={{ p: 2 }}>
            <DrawerSectionTitle icon={Tag} action={
              <Button size="small" startIcon={<Plus size={14} />} disabled={available.length === 0}
                onClick={(e) => setTagAnchor(e.currentTarget)}>Add</Button>
            }>Tags</DrawerSectionTitle>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {conv.tags.length === 0 && <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>No tags yet</Typography>}
              {conv.tags.map((t) => (
                <Chip key={t} label={t} size="small" onDelete={() => onRemoveTag(t)}
                  sx={{ fontWeight: 700, bgcolor: alpha(tagColor(t), 0.14), color: tagColor(t),
                    '& .MuiChip-deleteIcon': { color: tagColor(t) } }} />
              ))}
            </Box>
            <Menu anchorEl={tagAnchor} open={Boolean(tagAnchor)} onClose={() => setTagAnchor(null)}>
              {available.map((t) => (
                <MenuItem key={t.id} onClick={() => { onAddTag(t.name); setTagAnchor(null); }} sx={{ fontSize: 13 }}>
                  <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: t.color }} />
                  </ListItemIcon>
                  {t.name}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Divider />

          {/* Details */}
          <Box sx={{ p: 2 }}>
            <DrawerSectionTitle>Details</DrawerSectionTitle>
            <Stack spacing={1.5}>
              <DrawerDetailRow icon={conv.optIn === false ? XCircle : CheckCircle2} label="Opt-in">
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: conv.optIn === false ? 'error.main' : 'success.main' }}>
                  {conv.optIn === false ? 'Not opted in' : 'Opted in'}
                </Typography>
              </DrawerDetailRow>
              <DrawerDetailRow icon={Clock} label="Reply window">
                <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
                  {windowOpen && timeLeftLabel && <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{timeLeftLabel}</Typography>}
                  <Chip size="small" label={windowOpen ? 'Open' : 'Closed'}
                    color={windowOpen ? 'success' : 'warning'} sx={{ fontWeight: 700, height: 20 }} />
                </Stack>
              </DrawerDetailRow>
              {conv.linkedUser && (
                <DrawerDetailRow icon={Link2} label="Linked user">
                  <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: theme.palette.primary.main, maxWidth: 160 }}>{conv.linkedUser}</Typography>
                </DrawerDetailRow>
              )}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

const FMT_BUTTONS = [
  { icon: Bold, marker: '*', title: 'Bold' },
  { icon: Italic, marker: '_', title: 'Italic' },
  { icon: Strikethrough, marker: '~', title: 'Strikethrough' },
];

// Dedicated memoized composer to isolate typing re-renders from the heavy chat history & conversations list
const ChatComposer = React.memo(forwardRef(({
  active,
  composerActive,
  windowOpen,
  editingId,
  mobile,
  fullScreen,
  quickRepliesList,
  initialDraft,
  onConsumeInitialDraft,
  onSendMessage,
  onCancelEdit,
  onPickImageClick,
  onPickFileClick,
  onOpenTemplateDialog,
  aiGenerating,
  onAiSuggestReply,
  onAiChatSummary,
}, ref) => {
  const theme = useTheme();
  const [draft, setDraft] = useState('');
  const [qrAnchor, setQrAnchor] = useState(null);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [attachAnchor, setAttachAnchor] = useState(null);
  const inputRef = useRef(null);

  // Sync draft when editing message changes
  useEffect(() => {
    if (editingId && active?.messages) {
      const msg = active.messages.find((m) => m.id === editingId);
      if (msg) {
        setDraft(msg.text || '');
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    }
  }, [editingId, active?.messages]);

  // Clear or populate draft on active chat switch / initial draft change
  const prevActiveIdRef = useRef(active?.id);
  useEffect(() => {
    const isChatChanged = prevActiveIdRef.current !== active?.id;
    prevActiveIdRef.current = active?.id;

    if (initialDraft) {
      setDraft(initialDraft);
      if (onConsumeInitialDraft) onConsumeInitialDraft();
      requestAnimationFrame(() => inputRef.current?.focus());
    } else if (isChatChanged) {
      setDraft('');
    }
  }, [active?.id, initialDraft, onConsumeInitialDraft]);

  useImperativeHandle(ref, () => ({
    setDraft: (text) => {
      setDraft(text || '');
      requestAnimationFrame(() => inputRef.current?.focus());
    },
    insertText: (str) => {
      const el = inputRef.current;
      const pos = el?.selectionStart ?? draft.length;
      setDraft((prev) => prev.slice(0, pos) + str + prev.slice(pos));
      requestAnimationFrame(() => {
        if (el) {
          el.focus();
          el.setSelectionRange(pos + str.length, pos + str.length);
        }
      });
    },
    focus: () => inputRef.current?.focus(),
  }), [draft.length]);

  const wrapSelection = useCallback((marker) => {
    const el = inputRef.current;
    const s = el?.selectionStart ?? draft.length;
    const e = el?.selectionEnd ?? draft.length;
    const sel = draft.slice(s, e) || 'text';
    const next = draft.slice(0, s) + marker + sel + marker + draft.slice(e);
    setDraft(next);
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(s + marker.length, s + marker.length + sel.length);
      }
    });
  }, [draft]);

  const insertText = useCallback((str) => {
    const el = inputRef.current;
    const pos = el?.selectionStart ?? draft.length;
    setDraft((prev) => prev.slice(0, pos) + str + prev.slice(pos));
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(pos + str.length, pos + str.length);
      }
    });
  }, [draft.length]);

  const handleSend = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onSendMessage(trimmed);
    setDraft('');
    if (!mobile) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.repeat && !e.nativeEvent.isComposing && e.keyCode !== 229) {
      if (!mobile) {
        e.preventDefault();
        handleSend();
      }
    }
    if (e.key === 'Escape' && editingId) {
      onCancelEdit();
    }
  };

  const slotProps = useMemo(() => ({
    input: {
      startAdornment: (
        <InputAdornment position="start" sx={{ mr: { xs: 0, sm: 0.5 } }}>
          <Tooltip title="Emoji">
            <IconButton size="small" aria-label="Insert emoji" onClick={(e) => setEmojiAnchor(e.currentTarget)} sx={{ color: 'text.secondary', p: 0.5 }}>
              <Smile size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Quick replies">
            <IconButton size="small" aria-label="Quick replies" onClick={(e) => setQrAnchor(e.currentTarget)} sx={{ color: 'text.secondary', p: 0.5 }}>
              <Zap size={18} />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end" sx={{ ml: { xs: 0, sm: 0.5 } }}>
          <Tooltip title="Attach">
            <IconButton size="small" aria-label="Attach file" onClick={(e) => setAttachAnchor(e.currentTarget)} sx={{ color: 'text.secondary', p: 0.5 }}>
              <Paperclip size={18} />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      ),
      sx: {
        borderRadius: 3,
        pl: 1,
        pr: 1,
        bgcolor: theme.palette.mode === 'dark' ? '#2a3942' : '#ffffff',
        border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#0b141a', 0.08)}`,
        fontSize: { xs: '16px', sm: '13.5px' },
        fontFamily: 'Inter, sans-serif',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: alpha(WA_TEAL, 0.35),
        },
        '&.Mui-focused': {
          borderColor: WA_TEAL,
          bgcolor: theme.palette.mode === 'dark' ? '#2a3942' : '#ffffff',
          boxShadow: `0 0 0 2px ${alpha(WA_TEAL, 0.2)}`,
        }
      }
    }
  }), [theme.palette.mode]);

  return (
    <>
      {/* 24h window banner */}
      {!windowOpen && !editingId && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between"
          sx={{
            mx: { xs: 1, sm: 2 },
            px: 1.75,
            py: 0.85,
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            border: `1px solid ${theme.palette.divider}`,
            borderBottom: 'none',
            borderRadius: `${theme.shape.borderRadius * 4}px ${theme.shape.borderRadius * 4}px 0 0`,
          }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <Clock size={15} color={theme.palette.warning.dark} style={{ flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11.5, color: theme.palette.warning.dark, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
              24-hour reply window is closed. Custom messages may fail; sending a template is recommended.
            </Typography>
          </Stack>
          <Button
            size="small"
            variant="contained"
            startIcon={<FileText size={13} />}
            onClick={onOpenTemplateDialog}
            sx={{
              py: 0.25,
              px: 1.25,
              fontSize: 11.5,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 1.75,
              bgcolor: theme.palette.warning.dark,
              color: '#fff',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': {
                bgcolor: '#b45309',
              }
            }}
          >
            Use Template
          </Button>
        </Stack>
      )}

      {/* Editing banner */}
      {editingId && (
        <Stack direction="row" spacing={1} alignItems="center"
          sx={{
            mx: { xs: 1, sm: 2 },
            px: 1.75,
            py: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${theme.palette.divider}`,
            borderBottom: 'none',
            borderRadius: `${theme.shape.borderRadius * 4}px ${theme.shape.borderRadius * 4}px 0 0`,
          }}>
          <Pencil size={14} color={theme.palette.primary.main} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: theme.palette.primary.main, flex: 1, fontFamily: 'Inter, sans-serif' }}>Editing message</Typography>
          <IconButton size="small" onClick={onCancelEdit}><X size={15} /></IconButton>
        </Stack>
      )}

      {/* Floating Composer Container */}
      <Box sx={{
        px: { xs: 1, sm: 2 },
        pt: 0,
        pb: fullScreen
          ? 'calc(8px + env(safe-area-inset-bottom, 0px))'
          : { xs: 1, sm: 2 },
      }}>
        <Box
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? '#202c33' : '#f0f2f5',
            backdropFilter: 'blur(16px)',
            borderRadius: (!windowOpen || editingId)
              ? `0 0 ${theme.shape.borderRadius * 4}px ${theme.shape.borderRadius * 4}px`
              : 4,
            border: `1px solid ${theme.palette.divider}`,
            borderTop: (!windowOpen || editingId) ? 'none' : `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.24)'
              : '0 8px 32px rgba(145, 158, 171, 0.06)',
            overflow: 'hidden'
          }}
        >
          {/* Formatting toolbar & AI Strip */}
          {(composerActive || Boolean(draft.trim())) && (
            <>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap"
                  sx={{ 
                    px: 2, 
                    pt: 1, 
                    pb: 0.5,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                  }}>
                  {FMT_BUTTONS.map((b) => {
                    const Icon = b.icon;
                    return (
                      <Tooltip key={b.marker} title={b.title}>
                        <IconButton size="small" onClick={() => wrapSelection(b.marker)} sx={{ color: 'text.secondary' }}><Icon size={14} /></IconButton>
                      </Tooltip>
                    );
                  })}
                  <Tooltip title="Monospace">
                    <IconButton size="small" onClick={() => wrapSelection('```')} sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 800, fontFamily: 'monospace' }}>
                      {'</>'}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Insert link">
                    <IconButton size="small" onClick={() => insertText('https://')} sx={{ color: 'text.secondary' }}>
                      <Link2 size={14} />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ fontSize: 10, color: 'text.disabled', pr: 0.5, fontFamily: 'Inter, sans-serif' }}>
                    *bold* _italic_ ~strike~
                  </Typography>
                </Stack>
              </Box>

              {/* AI Action Strip */}
              <Box sx={{ px: 1.5, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                <Chip
                  label={aiGenerating ? "Generating..." : "✨ AI Suggest Reply"}
                  onClick={onAiSuggestReply}
                  disabled={aiGenerating}
                  size="small"
                  sx={{
                    fontWeight: 800,
                    fontSize: 11,
                    bgcolor: alpha('#6366F1', 0.12),
                    color: '#4338CA',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha('#6366F1', 0.22) }
                  }}
                  icon={aiGenerating ? <CircularProgress size={12} /> : undefined}
                />
                <Chip
                  label="📝 Summarize Chat"
                  onClick={onAiChatSummary}
                  disabled={aiGenerating}
                  size="small"
                  sx={{
                    fontWeight: 800,
                    fontSize: 11,
                    bgcolor: alpha(theme.palette.secondary.main || '#10b981', 0.1),
                    color: 'success.dark',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha('#10b981', 0.2) }
                  }}
                />
                <Typography sx={{ fontSize: 10, color: 'text.secondary', ml: 'auto', fontWeight: 600 }}>
                  Gemini Flash
                </Typography>
              </Box>
            </>
          )}

          <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="flex-end" sx={{ p: { xs: 1, sm: 1.5 } }}>
            {(composerActive || Boolean(draft.trim())) ? (
              <>
                <TextField
                  fullWidth 
                  size="small" 
                  multiline 
                  maxRows={mobile ? 3 : 4} 
                  placeholder={editingId ? 'Edit your message' : 'Type a message...'}
                  value={draft} 
                  onChange={(e) => setDraft(e.target.value)} 
                  inputRef={inputRef}
                  onKeyDown={handleKeyDown}
                  slotProps={slotProps}
                  sx={{ flex: 1 }}
                />
                <IconButton onClick={handleSend} disabled={!draft.trim()}
                  aria-label={editingId ? 'Save edited message' : 'Send message'}
                  sx={{ 
                    bgcolor: WA_TEAL, 
                    color: '#fff', 
                    width: { xs: 36, sm: 38 }, 
                    height: { xs: 36, sm: 38 }, 
                    flexShrink: 0,
                    boxShadow: `0 4px 12px ${alpha(WA_TEAL, 0.35)}`,
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: WA_GREEN, transform: 'scale(1.05)' }, 
                    '&.Mui-disabled': { bgcolor: alpha(theme.palette.action.disabledBackground, 0.6), color: theme.palette.action.disabled, boxShadow: 'none' } 
                  }}>
                  {editingId ? <Check size={18} /> : <Send size={16} />}
                </IconButton>
              </>
            ) : (
              <Button fullWidth variant="outlined" startIcon={<FileText size={16} />}
                onClick={onOpenTemplateDialog}
                sx={{ 
                  color: WA_TEAL, 
                  borderColor: alpha(WA_TEAL, 0.4),
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 13.5,
                  py: 1,
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: WA_TEAL,
                    bgcolor: alpha(WA_TEAL, 0.04),
                  }
                }}>
                Send a template message
              </Button>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Quick replies menu */}
      <Menu anchorEl={qrAnchor} open={Boolean(qrAnchor)} onClose={() => setQrAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ maxHeight: '60vh' }}>
        {quickRepliesList.map((q, i) => (
          <MenuItem key={i} onClick={() => { insertText(q); setQrAnchor(null); }} sx={{ fontSize: 13, maxWidth: { xs: '92vw', sm: 360 }, whiteSpace: 'normal' }}>{q}</MenuItem>
        ))}
      </Menu>

      {/* Attach menu */}
      <Menu anchorEl={attachAnchor} open={Boolean(attachAnchor)} onClose={() => setAttachAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ maxHeight: '60vh' }}>
        <MenuItem onClick={() => { onPickImageClick(); setAttachAnchor(null); }} sx={{ fontSize: 13 }}>
          <ListItemIcon sx={{ minWidth: 0, mr: 1 }}><ImageIcon size={16} /></ListItemIcon>Photos & Videos
        </MenuItem>
        <MenuItem onClick={() => { onPickFileClick(); setAttachAnchor(null); }} sx={{ fontSize: 13 }}>
          <ListItemIcon sx={{ minWidth: 0, mr: 1 }}><File size={16} /></ListItemIcon>Document
        </MenuItem>
      </Menu>

      {/* Emoji picker */}
      <Popover open={Boolean(emojiAnchor)} anchorEl={emojiAnchor} onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 1, width: { xs: 'calc(100vw - 32px)', sm: 280 }, maxWidth: 280, display: 'grid', gridTemplateColumns: { xs: 'repeat(7, 1fr)', sm: 'repeat(8, 1fr)' }, gap: 0.25 }}>
          {EMOJIS.map((emoji) => (
            <Box key={emoji} role="button" tabIndex={0} aria-label={`Insert ${emoji}`}
              onClick={() => insertText(emoji)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); insertText(emoji); } }}
              sx={{ fontSize: 20, textAlign: 'center', cursor: 'pointer', borderRadius: 1, py: 0.25,
                '&:hover, &:focus-visible': { bgcolor: alpha(theme.palette.primary.main, 0.1), outline: 'none' } }}>
              {emoji}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
}));

const WhatsAppInbox = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const mobile = useWaMobile();
  const vpHeight = useVisualViewportHeight();
  const isPwa = useIsPwa();
  const { activeConvoId, setActiveConvoId, setInboxActiveId } = useWaSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialDraftText, setInitialDraftText] = useState('');
  const isComponentMountedRef = useRef(true);
  const pendingPhoneActionRef = useRef('');
  useEffect(() => {
    isComponentMountedRef.current = true;
    return () => { isComponentMountedRef.current = false; };
  }, []);
  const [conversations, setConversations] = useState(SEED_CONVERSATIONS);
  const [activeId, setActiveId] = useState(() => {
    if (isMobile) return null;
    return localStorage.getItem('wa_active_convo_id') || null;
  });
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState('all'); // 'all', 'unread', 'awaiting'
  const [editingId, setEditingId] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollRef = useRef(null);
  const composerRef = useRef(null);
  const imgInputRef = useRef(null);
  const fileInputRef = useRef(null);
  // Mirror of conversations so the socket handler can tell whether an inbound
  // belongs to a conversation we've loaded, without a stale closure.
  const conversationsRef = useRef([]);
  // Mirror of the open conversation id so the socket handler reads the latest
  // value without the subscription effect needing `activeId` as a dependency
  // (which would re-fetch the whole list on every chat switch).
  const activeIdRef = useRef(activeId);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  const active = conversations.find((c) => c.id === activeId) || null;
  // True when a single chat takes over the whole phone screen (the app shell is
  // hidden by WhatsAppView). Declared with active check so full screen only activates
  // when an actual conversation is loaded and viewing.
  const fullScreen = isMobile && Boolean(active);

  // Hardware / gesture back-button handling on mobile devices when in active chat screen
  useEffect(() => {
    if (isMobile && active) {
      if (!window.history.state?.waChatOpen) {
        window.history.pushState({ waChatOpen: true }, '');
      }
      const handlePopState = () => {
        setActiveId(null);
      };
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isMobile, active]);

  const loadMoreConversations = () => {
    if (!hasMore || loadingMore || listLoading) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    waApi.getConversations({ page: nextPage, limit: 30 })
      .then((rows) => {
        if (!Array.isArray(rows) || rows.length === 0) {
          setHasMore(false);
          return;
        }
        setConversations((cur) => {
          const merged = [...cur];
          rows.forEach((row) => {
            if (!merged.some((x) => x.id === row.id)) {
              merged.push({ ...row, messages: [] });
            }
          });
          return merged;
        });
        setPage(nextPage);
        if (rows.length < 30) {
          setHasMore(false);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const handleListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadMoreConversations();
    }
  };
  // The conversation room this socket has currently joined, so we can leave it
  // when switching chats. Also a live handle to openConversation so a clicked
  // notification can open the chat (the function is defined below this effect).
  const joinedRoomRef = useRef(null);
  const openConvoRef = useRef(null);
  // Coalesces a burst of reconnect events into a single refresh (see below).
  const reconnectTimerRef = useRef(null);

  // New Chat States
  const [newChatDialogOpen, setNewChatDialogOpen] = useState(false);
  const [newChatContactId, setNewChatContactId] = useState('');
  const [newChatPhone, setNewChatPhone] = useState('');
  const [newChatName, setNewChatName] = useState('');
  const [contactsList, setContactsList] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [quickRepliesList, setQuickRepliesList] = useState(SEED_QUICK_REPLIES);

  // Template States
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [templatesList, setTemplatesList] = useState([]);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const unreadCount = useMemo(() => conversations.reduce((acc, c) => acc + (c.unread || 0), 0), [conversations]);

  const handleMarkAllRead = async () => {
    if (unreadCount === 0) return;
    setMarkingAllRead(true);
    setConversations((prev) => prev.map((c) => ({ ...c, unread: 0 })));
    try {
      const res = await waApi.markAllRead();
      setToast({
        open: true,
        msg: `Marked all unread messages as read${res?.marked ? ` (${res.marked} chats)` : ''}`,
        severity: 'success',
      });
    } catch (e) {
      console.error('Failed to mark all as read:', e);
      setToast({ open: true, msg: 'Failed to mark all as read', severity: 'error' });
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleMarkSingleRead = (convId, convName = '') => {
    setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, unread: 0 } : c)));
    waApi.markRead(convId).catch(() => {});
    setToast({ open: true, msg: `Marked ${convName || 'conversation'} as read`, severity: 'success' });
  };

  const windowOpen = active ? isWindowOpen(active.lastInboundAt) : false;

  const filtered = useMemo(() => {
    return conversations.filter((c) => {
      const matchesSearch = (c.name || "").toLowerCase().includes(search.toLowerCase()) || (c.phone || "").includes(search);
      if (!matchesSearch) return false;

      if (filterTab === 'unread') return c.unread > 0;
      if (filterTab === 'awaiting') {
        return c.unread > 0 || isWindowOpen(c.lastInboundAt);
      }
      return true;
    });
  }, [conversations, search, filterTab]);

  // Auto-scroll policy:
  //   • Opening a conversation (activeId changed) → always scroll to bottom.
  //   • Messages loaded from backend (threadLoading finished) → scroll to bottom.
  //   • New message added → scroll to bottom.
  const prevScrollRef = useRef({ id: null, len: 0 });
  const msgCount = active?.messages?.length || 0;

  const scrollToBottom = (instant = true) => {
    const doScroll = () => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    doScroll();
    requestAnimationFrame(doScroll);
    setTimeout(doScroll, 60);
    setTimeout(doScroll, 200);
  };

  useEffect(() => {
    const prev = prevScrollRef.current;
    const switchedChat = prev.id !== activeId;
    const hasNewMsgs = msgCount > prev.len;

    if (switchedChat || hasNewMsgs || !threadLoading) {
      scrollToBottom();
    }
    prevScrollRef.current = { id: activeId, len: msgCount };
  }, [activeId, msgCount, threadLoading]);

  const initialOpenRef = useRef(false);
  useEffect(() => {
    if (!initialOpenRef.current && conversations.length > 0) {
      if (searchParams.get('phone')) {
        return;
      }
      initialOpenRef.current = true;
      if (!isMobile) {
        const targetId = (activeId && conversations.some((c) => c.id === activeId))
          ? activeId
          : conversations[0]?.id;
        if (targetId) openConversation(targetId);
      } else if (activeId) {
        if (conversations.some((c) => c.id === activeId)) {
          openConversation(activeId);
        } else {
          setActiveId(null);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, isMobile]);

  useEffect(() => {
    const validId = active ? active.id : null;
    setInboxActiveId(validId);
    if (validId) {
      localStorage.setItem('wa_active_convo_id', validId);
    } else if (isMobile) {
      localStorage.removeItem('wa_active_convo_id');
    }
    return () => {
      setInboxActiveId(null);
    };
  }, [active, isMobile, setInboxActiveId]);

  // Load contacts & templates list for starting chats/sending templates
  useEffect(() => {
    waApi.getContacts()
      .then((data) => {
        if (Array.isArray(data?.contacts)) setContactsList(data.contacts);
      })
      .catch(() => {});

    waApi.getTemplates()
      .then((rows) => {
        if (Array.isArray(rows)) setTemplatesList(rows);
      })
      .catch(() => {});

    waApi.getTags()
      .then((rows) => {
        if (Array.isArray(rows)) setTagsList(rows);
      })
      .catch(() => {});

    waApi.getSettings()
      .then(({ settings }) => {
        if (settings && Array.isArray(settings.quick_replies)) {
          setQuickRepliesList(settings.quick_replies.length > 0 ? settings.quick_replies : SEED_QUICK_REPLIES);
        }
      })
      .catch(() => {});
  }, []);

  // Handle activeConvoId from context (tab transitions)
  useEffect(() => {
    if (activeConvoId) {
      setListLoading(true);
      setPage(1);
      setHasMore(true);
      waApi.getConversations({ page: 1, limit: 30 }).then((rows) => {
        if (Array.isArray(rows)) {
          setConversations(rows.map((c) => ({ ...c, messages: [] })));
          openConversation(activeConvoId);
        }
        if (rows.length < 30) {
          setHasMore(false);
        }
      }).catch(() => {})
        .finally(() => setListLoading(false));
      setActiveConvoId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConvoId]);

  // Handle direct contact / chat request from URL parameters (e.g. from Follow-ups CRM)
  useEffect(() => {
    const rawPhone = searchParams.get('phone');
    if (!rawPhone) {
      pendingPhoneActionRef.current = '';
      return;
    }

    const phone = normalizePhone(rawPhone);
    const rawName = searchParams.get('name') || '';
    const name = rawName === '—' ? '' : rawName;
    const text = searchParams.get('text') || '';

    if (!phone) return;

    const actionKey = `${phone}_${text}`;
    if (pendingPhoneActionRef.current === actionKey) return;
    pendingPhoneActionRef.current = actionKey;
    initialOpenRef.current = true;

    if (text) {
      setInitialDraftText(text);
    }

    (async () => {
      try {
        setListLoading(true);
        const res = await waApi.createConversation({ phone, name });
        if (!isComponentMountedRef.current) return;

        if (res?.conversation) {
          const conv = res.conversation;
          setConversations((prev) => {
            const exists = prev.some((c) => c.id === conv.id);
            if (exists) {
              return prev.map((c) => (c.id === conv.id ? { ...c, ...conv } : c));
            }
            return [conv, ...prev];
          });
          conversationsRef.current = [conv, ...conversationsRef.current.filter((c) => c.id !== conv.id)];

          openConversation(conv.id, { initialDraft: text });

          if (text && composerRef.current?.setDraft) {
            composerRef.current.setDraft(text);
          }

          // Clean up query params from URL so refresh does not re-trigger
          setTimeout(() => {
            if (isComponentMountedRef.current) {
              setSearchParams({}, { replace: true });
            }
          }, 400);
        } else {
          setToast({ open: true, msg: 'Could not open conversation for this contact.', severity: 'error' });
          pendingPhoneActionRef.current = '';
        }
      } catch (err) {
        if (!isComponentMountedRef.current) return;
        pendingPhoneActionRef.current = '';
        console.error('Failed to open WhatsApp conversation from URL:', err);
        const errMsg = err.response?.data?.error || err.response?.data?.msg || err.message || 'Could not open conversation';
        setToast({ open: true, msg: errMsg, severity: 'error' });
      } finally {
        if (isComponentMountedRef.current) setListLoading(false);
      }
    })();
  }, [searchParams, setSearchParams]);

  // Keep the ref in sync so the socket handler always sees the latest list.
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);

  // Pull the conversation list fresh, preserving messages we've already loaded.
  const refreshConversations = () => {
    setListLoading(true);
    setPage(1);
    setHasMore(true);
    waApi.getConversations({ page: 1, limit: 30 }).then((rows) => {
      if (!Array.isArray(rows)) return;
      setConversations((cur) => {
        const next = rows.map((c) => {
          const existing = cur.find((x) => x.id === c.id);
          return { ...c, messages: existing?.messages || [] };
        });
        // Retain any conversation that is in `cur` but not yet in `rows` (e.g. newly created deep-link chat)
        cur.forEach((c) => {
          if (!next.some((x) => x.id === c.id)) {
            next.push(c);
          }
        });
        return next;
      });
      if (rows.length < 30) {
        setHasMore(false);
      }
    }).catch(() => {})
      .finally(() => setListLoading(false));
  };

  // Load conversations from the backend and subscribe to live updates:
  //   • wa:message — scoped to the OPEN chat's room: append to that thread.
  //   • wa:inbox   — global: update the list (last message, unread, new chats)
  //                  and raise a desktop notification for inbound messages.
  //   • wa:status  — delivery-status ticks.
  useEffect(() => {
    refreshConversations();
    ensureNotifyPermission();

    // Append a message to its (open) thread. Room-scoped, so it only fires for
    // the conversation this socket has joined.
    const offMsg = onWaEvent('wa:message', ({ conversation_id, message }) => {
      const m = normalizeMsg(message);
      setConversations((prev) => prev.map((c) => {
        if (c.id !== conversation_id) return c;
        const msgs = c.messages || [];
        // Already have this message (by our id, by Meta's message id, or by the
        // correlation id we minted for the optimistic bubble) — ignore the dup.
        if (msgs.some((x) => x.id === m.id
          || (m.waMessageId && x.waMessageId && x.waMessageId === m.waMessageId)
          || (m.clientId && x.clientId && x.clientId === m.clientId && !String(x.id).startsWith('tmp-')))) return c;
        // Reconcile our own optimistic echo: match the temp outgoing bubble by
        // clientId first (robust), then fall back to same-text (legacy behavior).
        // Replace it in place instead of appending a copy.
        let tmpIdx = -1;
        if (m.dir === 'out') {
          if (m.clientId) {
            tmpIdx = msgs.findIndex((x) => String(x.id).startsWith('tmp-') && x.clientId === m.clientId);
          }
          if (tmpIdx < 0) {
            tmpIdx = msgs.findIndex((x) => String(x.id).startsWith('tmp-') && x.dir === 'out' && (x.text || '') === (m.text || ''));
          }
        }
        const nextMsgs = tmpIdx >= 0 ? msgs.map((x, i) => (i === tmpIdx ? m : x)) : [...msgs, m];
        const inbound = m.dir === 'in';
        return {
          ...c,
          messages: nextMsgs,
          // A customer reply re-opens the 24h window — keep lastInboundAt fresh
          // so the composer re-enables for the open chat too.
          ...(inbound ? { lastInboundAt: m.at || c.lastInboundAt } : {}),
        };
      }));
    });

    // Keep the conversation LIST live for every chat, even ones not open.
    const offInbox = onWaEvent('wa:inbox', ({ conversation_id, message }) => {
      const m = normalizeMsg(message);
      const isActive = conversation_id === activeIdRef.current;
      // Inbound to the chat we're looking at → already "read"; clear the
      // server-side unread counter it was incremented on receipt.
      if (m.dir === 'in' && isActive) waApi.markRead(conversation_id).catch(() => {});

      const known = conversationsRef.current.find((c) => c.id === conversation_id);
      // First-ever message from a new number → pull the list so the chat appears.
      if (!known) {
        refreshConversations();
      } else {
        const inbound = m.dir === 'in';
        setConversations((prev) => {
          const updated = prev.map((c) => (c.id === conversation_id ? {
            ...c, lastMessage: m.text || `[${m.type}]`,
            unread: isActive ? 0 : (c.unread || 0) + (inbound ? 1 : 0),
            // Re-open the 24h reply window on a customer reply.
            ...(inbound ? { lastInboundAt: m.at || c.lastInboundAt } : {}),
            // Mark stale so a non-active chat refetches its thread on reopen,
            // picking up messages that arrived while it wasn't the open chat.
            ...(inbound && !isActive ? { stale: true } : {}),
          } : c));
          // Move the just-updated conversation to the TOP (most-recent-active first).
          const idx = updated.findIndex((c) => c.id === conversation_id);
          if (idx > 0) {
            const [moved] = updated.splice(idx, 1);
            updated.unshift(moved);
          }
          return updated;
        });
      }

      // Notify on inbound, unless the agent is actively looking at that very
      // chat in a focused tab (no point pinging for what they're reading).
      if (m.dir === 'in' && (!isActive || document.hidden)) {
        notifyInbound({
          title: known?.name || 'New WhatsApp message',
          body: m.text || `[${m.type}]`,
          tag: `wa-${conversation_id}`,
          onOpen: () => openConvoRef.current?.(conversation_id),
        });
      }
    });

    const offStatus = onWaEvent('wa:status', ({ wa_message_id, status, errorCode, errorReason }) => {
      setConversations((prev) => prev.map((c) => ({
        ...c, messages: (c.messages || []).map((m) => (m.waMessageId === wa_message_id ? {
          ...m,
          status,
          ...(errorCode ? { errorCode } : {}),
          ...(errorReason ? { errorReason } : {}),
        } : m)),
      })));
    });

    // A message Meta accepted at send time but later DROPPED (e.g. marketing
    // pacing #131049, user stopped marketing #131050). Mark the bubble failed and
    // surface the reason so a "sent" message that never arrived isn't mistaken for
    // delivered.
    const offFailed = onWaEvent('wa:message_failed', ({ wa_message_id, code, reason }) => {
      setConversations((prev) => prev.map((c) => ({
        ...c, messages: (c.messages || []).map((m) => (m.waMessageId === wa_message_id ? {
          ...m,
          status: 'failed',
          errorCode: code ? String(code) : m.errorCode,
          errorReason: reason || m.errorReason,
        } : m)),
      })));
      setToast({ open: true, msg: `Message not delivered${code ? ` (#${code})` : ''}: ${reason || 'WhatsApp dropped it'}`, severity: 'error' });
    });

    // Lifecycle automation changed a contact's stage/tags → patch the matching
    // conversation in place so funnel tags update live without a refresh.
    const offContact = onWaEvent('wa:contact', ({ contactId, tags, stage, optIn }) => {
      setConversations((prev) => prev.map((c) => (c.contactId === contactId
        // Only overwrite tags/stage when the payload actually carries them — a
        // partial event must not blank c.tags (which addTagToActive later reads
        // with .includes and would throw on undefined).
        ? { ...c,
            tags: Array.isArray(tags) ? tags : c.tags,
            stage: stage !== undefined ? stage : c.stage,
            optIn: typeof optIn === 'boolean' ? optIn : c.optIn }
        : c)));
    });

    // Another admin/tab (or the backend) marked a conversation read → clear our
    // unread badge live so the count doesn't drift out of sync across tabs.
    const offRead = onWaEvent('wa:read', ({ conversation_id }) => {
      setConversations((prev) => prev.map((c) => (c.id === conversation_id ? { ...c, unread: 0 } : c)));
    });

    const offReadAll = onWaEvent('wa:read_all', () => {
      setConversations((prev) => prev.map((c) => ({ ...c, unread: 0 })));
    });

    // A contact was deleted (from Contacts & Tags) → drop its conversation(s) from
    // the inbox, and close the thread if it was the one open.
    const offDeleted = onWaEvent('wa:contact_deleted', ({ contactId, conversationIds = [] }) => {
      const ids = new Set(conversationIds);
      setConversations((prev) => prev.filter((c) => c.contactId !== contactId && !ids.has(c.id)));
      setActiveId((prev) => (ids.has(prev) ? null : prev));
    });
    // After a reconnect (e.g. server redeploy), socket.io delivered nothing while
    // we were offline — re-fetch the list and re-join + reload the open thread so
    // missed messages appear without a manual refresh. Reconnects can fire in a
    // rapid burst (flaky network, server restart) so debounce them into a single
    // refresh ~1.5s after the last one instead of hammering the API per event.
    const offReconnect = onWaReconnect(() => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        refreshConversations();
        const active = joinedRoomRef.current;
        if (active) { joinWaRoom(active); openConvoRef.current?.(active, { force: true }); }
      }, 1500);
    });
    return () => {
      offMsg(); offInbox(); offStatus(); offFailed(); offContact(); offRead(); offReadAll(); offDeleted(); offReconnect();
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
    };
    // Subscribe once on mount. Handlers read live values via refs, so this must
    // NOT re-run on every chat switch (that re-subscribed the socket per click).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openConversation = (id, opts = {}) => {
    // Move the socket's room subscription to this chat so its live messages
    // (wa:message) arrive while it's open. Leave the previous chat's room.
    if (joinedRoomRef.current && joinedRoomRef.current !== id) leaveWaRoom(joinedRoomRef.current);
    if (id) joinWaRoom(id);
    joinedRoomRef.current = id;

    setActiveId(id);
    setEditingId(null);
    if (opts.initialDraft !== undefined) {
      setInitialDraftText(opts.initialDraft);
    }
    composerRef.current?.setDraft(opts.initialDraft !== undefined ? opts.initialDraft : '');
    setProfileOpen(false);
    // Clear unread AND the stale flag — we're about to (re)load this thread.
    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, unread: 0, stale: false } : c)));
    waApi.markRead(id).catch(() => {});

    // Show the cached thread instantly. Only hit the network the first time a
    // conversation is opened, when it has no messages cached yet, OR when it was
    // flagged stale (a message arrived while it wasn't the open chat) — otherwise
    // flipping between chats is immediate instead of re-fetching every click.
    const cached = conversationsRef.current.find((c) => c.id === id);
    const hasCache = cached && Array.isArray(cached.messages) && cached.messages.length > 0;
    // `opts.force` bypasses the cache — used on reconnect so the OPEN thread
    // reloads and picks up messages that arrived while the socket was down
    // (socket.io buffers nothing across a disconnect).
    if (hasCache && !cached.stale && !opts.force) return;

    setThreadLoading(true);
    waApi.getMessages(id)
      .then((data) => {
        // getMessages now returns { messages, hasMore, nextBefore }; tolerate the
        // legacy bare-array shape too. Seed the pagination cursor for load-older.
        const msgs = Array.isArray(data?.messages) ? data.messages : (Array.isArray(data) ? data : null);
        if (msgs) {
          const norm = msgs.map(normalizeMsg);
          const latestInbound = [...norm].reverse().find((m) => m.dir === 'in');
          setConversations((prev) => prev.map((c) => (c.id === id
            ? {
                ...c,
                messages: norm,
                stale: false,
                threadHasMore: !!data?.hasMore,
                oldestCursor: data?.nextBefore || null,
                lastInboundAt: (latestInbound && latestInbound.at) ? latestInbound.at : c.lastInboundAt,
              }
            : c)));
        }
      })
      .catch(() => {})
      .finally(() => {
        setThreadLoading(false);
        scrollToBottom();
      });
  };

  // Load the previous page of older messages for a thread and PREPEND them,
  // preserving the scroll position so the view doesn't jump. Driven by the
  // thread's onScroll when the user nears the top. Re-entrancy-guarded.
  const loadingOlderRef = useRef(false);
  const loadOlderMessages = (convId) => {
    const conv = conversationsRef.current.find((c) => c.id === convId);
    if (!conv || !conv.threadHasMore || !conv.oldestCursor || loadingOlderRef.current) return;
    loadingOlderRef.current = true;
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    const prevTop = el ? el.scrollTop : 0;
    waApi.getMessages(convId, { before: conv.oldestCursor })
      .then((data) => {
        const older = (Array.isArray(data?.messages) ? data.messages : []).map(normalizeMsg);
        if (!older.length) {
          setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, threadHasMore: false } : c)));
          return;
        }
        setConversations((prev) => prev.map((c) => (c.id === convId
          ? { ...c, messages: [...older, ...(c.messages || [])], threadHasMore: !!data?.hasMore, oldestCursor: data?.nextBefore || null }
          : c)));
        // After the DOM grows, keep the same message under the user's eyes by
        // adding the height delta to scrollTop (prepend must not scroll-jump).
        requestAnimationFrame(() => {
          const e2 = scrollRef.current;
          if (e2) e2.scrollTop = prevTop + (e2.scrollHeight - prevHeight);
        });
      })
      .catch(() => {})
      .finally(() => { loadingOlderRef.current = false; });
  };

  // Keep a live handle for the notification-click handler (which is bound once,
  // on mount), and leave whatever room we're in when the inbox unmounts.
  useEffect(() => { openConvoRef.current = openConversation; });
  useEffect(() => () => { if (joinedRoomRef.current) leaveWaRoom(joinedRoomRef.current); }, []);

  const handleCreateNewChat = async () => {
    let payload;
    if (newChatContactId) {
      payload = { contactId: newChatContactId };
    } else {
      const phone = normalizePhone(newChatPhone);
      if (!phone) {
        setToast({ open: true, msg: 'Enter a valid phone number (with country code, e.g. 919409343754).', severity: 'error' });
        return;
      }
      payload = { phone, name: newChatName.trim() };
    }
    try {
      const res = await waApi.createConversation(payload);
      if (res?.conversation) {
        // Add to conversations if not already present
        setConversations((prev) => {
          if (prev.some((c) => c.id === res.conversation.id)) return prev;
          return [res.conversation, ...prev];
        });
        // Set as active
        openConversation(res.conversation.id);
        // Close dialog and reset fields
        setNewChatDialogOpen(false);
        setNewChatContactId('');
        setNewChatPhone('');
        setNewChatName('');
      } else {
        setToast({ open: true, msg: 'Could not start the chat. Please try again.', severity: 'error' });
      }
    } catch (err) {
      console.error("Failed to start chat:", err);
      const errorMsg = err.response?.data?.error || err.response?.data?.msg || err.message || 'Failed to start chat';
      setToast({ open: true, msg: errorMsg, severity: 'error' });
    }
  };

  const handleSendTemplate = async () => {
    if (!selectedTemplateName || !active) return;
    const convId = active.id;
    const t = templatesList.find(x => x.name === selectedTemplateName);
    const lang = t?.language || 'en_US';

    const clientId = makeClientId();
    const tmp = { id: `tmp-${clientId}`, clientId, dir: 'out', text: `[template] ${selectedTemplateName}`, at: new Date().toISOString(), status: 'pending', type: 'template', templateName: selectedTemplateName };
    setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, messages: [...(c.messages || []), tmp], lastMessage: `[template] ${selectedTemplateName}` } : c)));

    setTemplateDialogOpen(false);
    setSelectedTemplateName('');

    try {
      const { message } = await waApi.sendMessage(convId, { type: 'template', templateName: selectedTemplateName, language: lang, clientId });
      if (message) {
        reconcileSent(convId, tmp, message);
        setToast({ open: true, msg: 'Template sent successfully', severity: 'success' });
      }
    } catch (e) {
      const errorMsg = e.response?.data?.error || e.response?.data?.msg || e.message;
      setToast({ open: true, msg: `Failed to send: ${errorMsg}`, severity: 'error' });
      patchMessages(convId, (msgs) => msgs.filter((m) => m.id !== tmp.id));
    }
  };

  // Patch a SPECIFIC conversation's messages. Each async send/reconcile captures
  // the conversation id it started in, so a resolve that lands after the agent
  // switched chats writes to the right thread (and never throws on null active).
  const patchMessages = (convId, updater) => {
    if (!convId) return;
    setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, messages: updater(c.messages || []) } : c)));
  };

  // Reconcile an optimistic outgoing bubble with the server's saved message:
  // replace the temp bubble in place, matching by clientId first (robust) and
  // falling back to the temp id; also dedup if the live wa:message echo already
  // appended the real message (match by real id / waMessageId / clientId).
  const reconcileSent = (convId, tmp, message, opts = {}) => {
    if (!convId || !message) return;
    const real = opts.alreadyNormalized ? message : normalizeMsg(message);
    patchMessages(convId, (msgs) => {
      // Did the socket echo already add the real message? If so, drop the temp.
      const echoIdx = msgs.findIndex((m) => m.id !== tmp.id && (
        m.id === real.id ||
        (real.waMessageId && m.waMessageId && m.waMessageId === real.waMessageId) ||
        (tmp.clientId && m.clientId === tmp.clientId)
      ));
      if (echoIdx >= 0) {
        return msgs.filter((m) => m.id !== tmp.id);
      }
      // Otherwise replace the temp bubble in place (match by clientId, then id).
      const tmpIdx = msgs.findIndex((m) => (tmp.clientId && m.clientId === tmp.clientId) || m.id === tmp.id);
      if (tmpIdx >= 0) return msgs.map((m, i) => (i === tmpIdx ? real : m));
      // Temp not found (already removed) — append only if not a duplicate.
      if (msgs.some((m) => m.id === real.id)) return msgs;
      return [...msgs, real];
    });
  };

  const sendMessage = (text) => {
    const body = (typeof text === 'string' ? text : '').trim();
    if (!body || !active) return;
    const convId = active.id;
    if (editingId) {
      // NOTE: Cloud API can't edit a delivered message — updates the inbox copy only.
      patchMessages(convId, (msgs) => msgs.map((m) => (m.id === editingId ? { ...m, text: body, edited: true } : m)));
      setEditingId(null);
      composerRef.current?.setDraft('');
      return;
    }
    const clientId = makeClientId();
    const tmp = { id: `tmp-${clientId}`, clientId, dir: 'out', text: body, at: new Date().toISOString(), status: 'pending', type: 'text' };
    setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, messages: [...(c.messages || []), tmp], lastMessage: body } : c)));
    composerRef.current?.setDraft('');
    // Send via backend (simulated until WHATSAPP_LIVE_SEND=on). Reconcile the temp
    // bubble with the saved message id/status on success.
    waApi.sendMessage(convId, { type: 'text', text: body, previewUrl: true, clientId })
      .then(({ message }) => reconcileSent(convId, tmp, message))
      .catch(() => {
        // Don't pretend it sent — mark it failed so the agent can retry.
        patchMessages(convId, (msgs) => msgs.map((m) => (m.id === tmp.id ? { ...m, status: 'failed' } : m)));
        setToast({ open: true, msg: 'Message failed to send. Tap the message to retry.', severity: 'error' });
      });
  };

  // Resend a message that previously failed.
  const retryMessage = (msg) => {
    if (!active || !msg?.text) return;
    const convId = active.id;
    const clientId = msg.clientId || makeClientId();
    patchMessages(convId, (msgs) => msgs.map((m) => (m.id === msg.id ? { ...m, status: 'pending', clientId } : m)));
    waApi.sendMessage(convId, { type: 'text', text: msg.text, previewUrl: true, clientId })
      .then(({ message }) => reconcileSent(convId, { ...msg, clientId }, message))
      .catch(() => patchMessages(convId, (msgs) => msgs.map((m) => (m.id === msg.id ? { ...m, status: 'failed' } : m))));
  };

  const addMediaMessage = (media, fileObj) => {
    if (!active) return;
    const convId = active.id;
    const preview = media.type === 'file' ? `📄 ${media.mediaName}` : media.type === 'video' ? '🎥 Video' : '📷 Photo';
    const clientId = makeClientId();
    const tmp = { id: `tmp-${clientId}`, clientId, dir: 'out', at: new Date().toISOString(), status: 'pending', ...media };
    setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, messages: [...(c.messages || []), tmp], lastMessage: preview } : c)));
    waApi.sendMessage(convId, { type: media.type, file: fileObj, caption: media.caption || '', filename: media.mediaName, clientId })
      .then(({ message }) => {
        const normalized = normalizeMsg(message);
        // Prefer the real server URL for display; only fall back to the local blob
        // (the optimistic preview) when the server didn't return a URL.
        const localUrl = tmp.mediaUrl;
        const serverUrl = normalized.mediaUrl;
        const preferredUrl = serverUrl || localUrl;
        // The optimistic bubble held an object URL (URL.createObjectURL). Once the
        // server URL replaces it, revoke the blob so it isn't leaked for the tab's
        // lifetime (repeated media sends would otherwise bloat memory).
        if (serverUrl && localUrl && localUrl.startsWith('blob:')) URL.revokeObjectURL(localUrl);
        const merged = { ...normalized, mediaUrl: preferredUrl };
        reconcileSent(convId, tmp, merged, { alreadyNormalized: true });
      })
      .catch((err) => {
        console.error("Failed to send media message:", err);
        patchMessages(convId, (msgs) => msgs.map((m) => (m.id === tmp.id ? { ...m, status: 'failed' } : m)));
      });
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      addMediaMessage({
        type: file.type.startsWith('video') ? 'video' : 'image',
        mediaName: file.name,
        mediaUrl: URL.createObjectURL(file),
        mediaSize: formatSize(file.size)
      }, file);
    }
    e.target.value = '';
  };
  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      addMediaMessage({
        type: 'file',
        mediaName: file.name,
        mediaUrl: URL.createObjectURL(file),
        mediaSize: formatSize(file.size)
      }, file);
    }
    e.target.value = '';
  };

  const startEdit = (msg) => { setEditingId(msg.id); composerRef.current?.setDraft(msg.text || ''); };
  const cancelEdit = () => { setEditingId(null); composerRef.current?.setDraft(''); };

  const requestDelete = (msg) => setDeleteTarget(msg);
  const confirmDelete = () => {
    const msg = deleteTarget;
    if (!msg || !active) return;
    const convId = active.id;
    if (editingId === msg.id) cancelEdit();
    // NOTE: Cloud API has no delete endpoint — inbox-only tombstone.
    patchMessages(convId, (msgs) => msgs.map((m) => (m.id === msg.id ? { ...m, deleted: true, edited: false } : m)));
    setDeleteTarget(null);
    // Persist the tombstone. Without this the flag lived only in component state,
    // so the message reappeared the next time the thread was re-fetched. Temp
    // (unsent) bubbles have no server id yet, so those stay inbox-only.
    if (!String(msg.id).startsWith('tmp-')) {
      waApi.deleteMessage(convId, msg.id).catch(() => {
        setToast({ open: true, msg: 'Could not delete on the server — it may reappear after a refresh.', severity: 'error' });
      });
    }
  };

  // Copy the contact's number without opening the profile drawer.
  const copyNumber = (phone) => {
    if (!phone) return;
    navigator.clipboard?.writeText(phone)
      .then(() => setToast({ open: true, msg: 'Number copied', severity: 'success' }))
      .catch(() => setToast({ open: true, msg: 'Could not copy number', severity: 'error' }));
  };

  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);

  const handleAiSuggestReply = async () => {
    if (!active?.phone) return;
    setAiGenerating(true);
    try {
      const res = await waApi.getAiSuggestedReply(active.phone);
      if (res.success && res.text) {
        composerRef.current?.setDraft(res.text);
        setToast({ open: true, msg: '✨ AI response generated!', severity: 'success' });
      } else {
        setToast({ open: true, msg: res.error || 'Could not generate AI reply.', severity: 'error' });
      }
    } catch (e) {
      setToast({ open: true, msg: 'AI suggestion failed.', severity: 'error' });
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAiChatSummary = async () => {
    if (!active?.phone) return;
    setAiGenerating(true);
    try {
      const res = await waApi.getAiChatSummary(active.phone);
      if (res.success && res.summary) {
        setAiSummary(res.summary);
      } else {
        setToast({ open: true, msg: res.error || 'Could not generate chat summary.', severity: 'error' });
      }
    } catch (e) {
      setToast({ open: true, msg: 'Summary failed.', severity: 'error' });
    } finally {
      setAiGenerating(false);
    }
  };

  const addTagToActive = (tagName) => {
    if (!active) return;
    const convId = active.id;
    setConversations((prev) => prev.map((c) => (c.id === convId && !c.tags.includes(tagName) ? { ...c, tags: [...c.tags, tagName] } : c)));
    if (active?.contactId) waApi.addContactTag(active.contactId, tagName).catch(() => {});
  };
  const removeTagFromActive = (tagName) => {
    if (!active) return;
    const convId = active.id;
    setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, tags: c.tags.filter((t) => t !== tagName) } : c)));
    if (active?.contactId) waApi.removeContactTag(active.contactId, tagName).catch(() => {});
  };

  // Manually move the active contact through the funnel. The backend also emits
  // wa:contact, which patches the tags; we set stage optimistically here.
  const changeContactStage = (stage) => {
    if (!active?.contactId) return;
    const convId = active.id;
    setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, stage } : c)));
    waApi.setContactStage(active.contactId, stage)
      .then((res) => {
        if (res?.contact) setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, tags: res.contact.tags, stage: res.contact.stage } : c)));
      })
      .catch(() => setToast({ open: true, msg: 'Failed to update stage', severity: 'error' }));
  };

  // Send the "pick a call time" interactive prompt. The customer's button tap
  // (handled by the webhook) is what advances them to Call Pending.
  const sendCallSchedule = async () => {
    if (!active) return;
    const convId = active.id;
    try {
      const { message } = await waApi.sendCallSchedule(convId);
      if (message) {
        patchMessages(convId, (msgs) => (msgs.some((m) => m.id === String(message.id)) ? msgs : [...msgs, normalizeMsg(message)]));
        setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, lastMessage: message.text } : c)));
        setToast({ open: true, msg: 'Call-schedule prompt sent', severity: 'success' });
      }
    } catch (e) {
      const errorMsg = e.response?.data?.error || e.response?.data?.msg || e.message;
      setToast({ open: true, msg: `Failed to send: ${errorMsg}`, severity: 'error' });
    }
  };

  const composerActive = windowOpen || Boolean(editingId);

  const listPane = (
    <Box sx={{ width: { xs: '100%', sm: 300, md: 340 }, flexShrink: 0, borderRight: { md: `1px solid ${theme.palette.divider}` },
      display: { xs: active && isMobile ? 'none' : 'flex', md: listCollapsed ? 'none' : 'flex' }, flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
          <TextField fullWidth size="small" placeholder="Search name or number" value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start"><Search size={16} /></InputAdornment>,
                sx: { 
                  borderRadius: 2.5, 
                  bgcolor: theme.palette.background.neutral,
                  fontSize: { xs: '16px', sm: '13.5px' },
                  '&.Mui-focused': {
                    boxShadow: `0 0 0 2px ${alpha(WA_TEAL, 0.25)}`,
                    borderColor: WA_TEAL
                  }
                }
              }
            }} />
          <Tooltip title="New Chat">
            <IconButton color="primary" onClick={() => setNewChatDialogOpen(true)}
              sx={{ 
                bgcolor: alpha(WA_TEAL, 0.08), 
                color: WA_TEAL,
                '&:hover': { bgcolor: alpha(WA_TEAL, 0.15) },
                transition: 'all 0.2s'
              }}>
              <Plus size={18} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pb: 0.5 }}>
          <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
            {['all', 'unread', 'awaiting'].map((tabKey) => {
              const activeTab = filterTab === tabKey;
              const labels = {
                all: 'All',
                unread: unreadCount > 0 ? `Unread (${unreadCount})` : 'Unread',
                awaiting: 'Awaiting',
              };
              return (
                <Chip
                  key={tabKey}
                  label={labels[tabKey]}
                  size="small"
                  onClick={() => setFilterTab(tabKey)}
                  sx={{
                    fontWeight: activeTab ? 700 : 500,
                    fontSize: 11.5,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    bgcolor: activeTab 
                      ? (theme.palette.mode === 'dark' ? alpha(WA_TEAL, 0.25) : alpha(WA_TEAL, 0.08))
                      : 'transparent',
                    color: activeTab ? WA_TEAL : 'text.secondary',
                    border: `1px solid ${activeTab ? alpha(WA_TEAL, 0.3) : alpha(theme.palette.divider, 0.5)}`,
                    '&:hover': {
                      bgcolor: activeTab 
                        ? (theme.palette.mode === 'dark' ? alpha(WA_TEAL, 0.3) : alpha(WA_TEAL, 0.12))
                        : alpha(theme.palette.action.hover, 0.4)
                    },
                    transition: 'all 0.2s'
                  }}
                />
              );
            })}
          </Stack>

          {unreadCount > 0 && (
            <Tooltip title="Mark all unread conversations as read">
              <span>
                <Button
                  size="small"
                  variant="text"
                  disabled={markingAllRead}
                  onClick={handleMarkAllRead}
                  startIcon={markingAllRead ? <CircularProgress size={12} color="inherit" /> : <CheckCheck size={14} />}
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'none',
                    color: WA_TEAL,
                    p: '2px 6px',
                    minWidth: 'auto',
                    borderRadius: 1.5,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    ml: 0.5,
                    '&:hover': { bgcolor: alpha(WA_TEAL, 0.08) },
                  }}
                >
                  Mark read
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>
      <Divider />
      <Box onScroll={handleListScroll} sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {listLoading ? (
          Array.from({ length: 6 }).map((_, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.divider, 0.65)}` }}>
              <Skeleton variant="circular" width={44} height={44} sx={{ flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Skeleton variant="text" width="55%" height={15} />
                  <Skeleton variant="text" width="20%" height={12} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Skeleton variant="text" width="75%" height={12} />
                  <Skeleton variant="circular" width={8} height={8} />
                </Stack>
              </Box>
            </Box>
          ))
        ) : filtered.length === 0 ? (
          <Stack alignItems="center" sx={{ py: 6, color: 'text.disabled' }} spacing={1}>
            <MessageCircle size={32} /><Typography sx={{ fontSize: 13 }}>No conversations</Typography>
          </Stack>
        ) : (
          <>
            {filtered.map((conv) => (
              <ConversationRow
                key={conv.id}
                conv={conv}
                active={conv.id === activeId}
                onClick={() => openConversation(conv.id)}
                onMarkRead={handleMarkSingleRead}
              />
            ))}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} color="primary" />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );

  const chatPane = (
    <Box sx={{ 
      flex: 1, 
      display: { xs: active ? 'flex' : 'none', md: 'flex' }, 
      flexDirection: 'column', 
      minHeight: 0,
      bgcolor: theme.palette.mode === 'dark' ? '#0b141a' : '#efeae2',
      backgroundImage: theme.palette.mode === 'dark' 
        ? 'radial-gradient(rgba(255, 255, 255, 0.04) 1.5px, transparent 1.5px)' 
        : 'radial-gradient(rgba(11, 20, 26, 0.05) 1.5px, transparent 1.5px)', 
      backgroundSize: '20px 20px' 
    }}>
      {!active ? (
        <Stack flex={1} alignItems="center" justifyContent="center" spacing={3.5} 
          sx={{ 
            color: 'text.disabled', 
            textAlign: 'center', 
            px: 4,
            py: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: theme.palette.mode === 'dark' 
              ? `radial-gradient(circle at 50% 50%, ${alpha(WA_TEAL, 0.08)} 0%, transparent 60%)` 
              : `radial-gradient(circle at 50% 50%, ${alpha(WA_TEAL, 0.04)} 0%, transparent 60%)`
          }}>
          
          {/* Animated Glow Ring Icon */}
          <Box sx={{ position: 'relative', width: 88, height: 88, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto' }}>
            <Box sx={{
              position: 'absolute',
              top: -12,
              left: -12,
              right: -12,
              bottom: -12,
              borderRadius: '32px',
              bgcolor: alpha(WA_TEAL, 0.04),
              animation: 'pulseGlow 2.5s infinite ease-in-out',
              zIndex: 0,
              '@keyframes pulseGlow': {
                '0%': { transform: 'scale(0.95)', opacity: 0.5 },
                '50%': { transform: 'scale(1.05)', opacity: 1 },
                '100%': { transform: 'scale(0.95)', opacity: 0.5 }
              }
            }} />
            <Box sx={{ 
              position: 'relative',
              width: '100%', 
              height: '100%', 
              borderRadius: '26px', 
              bgcolor: theme.palette.mode === 'dark' ? alpha(WA_TEAL, 0.15) : alpha(WA_TEAL, 0.08), 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              boxShadow: theme.palette.mode === 'dark'
                ? `0 12px 32px 0 ${alpha(WA_TEAL, 0.25)}`
                : `0 12px 32px 0 ${alpha(WA_TEAL, 0.15)}`,
              border: `1px solid ${alpha(WA_TEAL, 0.25)}`,
              zIndex: 1
            }}>
              <WhatsAppIcon sx={{ fontSize: 44, color: WA_TEAL }} />
            </Box>
          </Box>

          <Box sx={{ zIndex: 1, width: '100%', textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary', fontFamily: 'Outfit, sans-serif', mb: 1, letterSpacing: -0.5, textAlign: 'center' }}>
              Select a conversation
            </Typography>
            <Typography sx={{ fontSize: 13, maxWidth: 360, color: 'text.secondary', fontFamily: 'Inter, sans-serif', lineHeight: 1.6, mx: 'auto', textAlign: 'center' }}>
              Select a chat from the sidebar to view the conversation thread, manage tags, and reply to customers in real-time.
            </Typography>
          </Box>

          {/* Quick Feature Highlights */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center" sx={{ width: '100%', maxWidth: 500, mt: 1, zIndex: 1, mx: 'auto' }}>
            <Card sx={{ 
              flex: 1, 
              p: 2, 
              borderRadius: 2.5, 
              border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.divider, 0.75)}`,
              bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.4) : '#ffffff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.015)',
              transition: 'all 0.2s ease',
              '&:hover': { transform: 'translateY(-2px)' }
            }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: alpha(theme.palette.success.main, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, mx: 'auto' }}>
                <Zap size={16} color={theme.palette.success.main} />
              </Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, textAlign: 'center', mb: 0.5, color: 'text.primary' }}>Auto Messages</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', textAlign: 'center', lineHeight: 1.45 }}>Greet new users and reply to keywords automatically.</Typography>
            </Card>

            <Card sx={{ 
              flex: 1, 
              p: 2, 
              borderRadius: 2.5, 
              border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.divider, 0.75)}`,
              bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.4) : '#ffffff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.015)',
              transition: 'all 0.2s ease',
              '&:hover': { transform: 'translateY(-2px)' }
            }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, mx: 'auto' }}>
                <FileText size={16} color={theme.palette.primary.main} />
              </Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, textAlign: 'center', mb: 0.5, color: 'text.primary' }}>Templates & Tags</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', textAlign: 'center', lineHeight: 1.45 }}>Keep contacts organized and dispatch pre-approved templates.</Typography>
            </Card>
          </Stack>

          {!isMobile && listCollapsed && (
            <Button variant="outlined" size="small" startIcon={<PanelLeftOpen size={16} />}
              onClick={() => setListCollapsed(false)} sx={{ mt: 1, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
              Show chats
            </Button>
          )}
        </Stack>
      ) : (
        <>
          {/* Chat header */}
          <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}
            sx={{
              px: { xs: 1, sm: 2.5 },
              py: { xs: 0.75, sm: 1.5 },
              // When full-screen on a phone the app bar is hidden, so this header
              // sits at the very top — pad it past the notch / status bar.
              pt: fullScreen ? (isPwa ? 'calc(14px + env(safe-area-inset-top, 0px))' : 'calc(10px + env(safe-area-inset-top, 0px))') : undefined,
              bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.85) : alpha('#ffffff', 0.9),
              backdropFilter: 'blur(8px)',
              borderBottom: `1px solid ${theme.palette.divider}`,
              zIndex: 5
            }}>
            {isMobile && (
              <IconButton
                onClick={() => {
                  if (window.history.state?.waChatOpen) {
                    window.history.back();
                  } else {
                    setActiveId(null);
                  }
                }}
                aria-label="Back to conversations"
                sx={{ p: 0.75, color: 'text.secondary', mr: 0.25 }}
              >
                <ArrowLeft size={20} />
              </IconButton>
            )}
            {!isMobile && (
              <Tooltip title={listCollapsed ? 'Show chats' : 'Hide chats'}>
                <IconButton size="small" aria-label={listCollapsed ? 'Show chat list' : 'Hide chat list'}
                  onClick={() => setListCollapsed((v) => !v)}
                  sx={{ color: 'text.secondary' }}>
                  {listCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                </IconButton>
              </Tooltip>
            )}
            <Box onClick={() => setProfileOpen(true)} sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flex: 1, minWidth: 0, cursor: 'pointer' }}>
              <Avatar sx={{ 
                width: { xs: 36, sm: 42 }, 
                height: { xs: 36, sm: 42 }, 
                bgcolor: WA_TEAL, 
                color: '#fff', 
                fontWeight: 800,
                fontSize: { xs: 13, sm: 15 },
                fontFamily: 'Outfit, sans-serif',
                boxShadow: `0 4px 10px ${alpha(WA_TEAL, 0.2)}`
              }}>{initialsOf(active.name)}</Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="nowrap">
                  <Typography noWrap sx={{ fontSize: { xs: 13, sm: 14.5 }, fontWeight: 800, fontFamily: 'Outfit, sans-serif', minWidth: 0 }}>
                    {active.name}
                  </Typography>
                  <Chip
                    label={windowOpen ? (isMobile ? 'Open' : 'Reply Window Open') : (isMobile ? 'Templates' : 'Templates Only')}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: { xs: 9.5, sm: 10 },
                      fontWeight: 800,
                      fontFamily: 'Inter, sans-serif',
                      bgcolor: windowOpen ? alpha(WA_GREEN, 0.12) : alpha(theme.palette.warning.main, 0.1),
                      color: windowOpen ? (theme.palette.mode === 'dark' ? WA_GREEN : 'success.dark') : 'warning.dark',
                      border: `1px solid ${windowOpen ? alpha(WA_GREEN, 0.3) : alpha(theme.palette.warning.main, 0.3)}`,
                      borderRadius: 1,
                      px: 0.5,
                      flexShrink: 0
                    }}
                  />
                </Stack>
                <Stack 
                  direction="row" 
                  alignItems="center" 
                  spacing={0.5} 
                  sx={{ mt: 0.15 }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: { xs: 10.5, sm: 11 }, color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>{active.phone}</Typography>
                    <Tooltip title="Copy number">
                      <IconButton size="small" aria-label="Copy number"
                        onClick={(e) => { e.stopPropagation(); copyNumber(active.phone); }}
                        sx={{ p: 0.15, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}>
                        <Copy size={10} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  {active.tags && active.tags.length > 0 && !isMobile && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 0.5, flexShrink: 0, overflow: 'hidden' }}>
                      {active.tags.slice(0, 3).map((t) => (
                        <Chip key={t} label={t} size="small" 
                          sx={{
                            height: 18,
                            fontSize: 9.5,
                            fontWeight: 800,
                            fontFamily: 'Inter, sans-serif',
                            bgcolor: alpha(WA_TEAL, 0.1),
                            color: WA_TEAL,
                            borderRadius: 0.75,
                            maxWidth: 110,
                            '& .MuiChip-label': { px: 0.6, overflow: 'hidden', textOverflow: 'ellipsis' }
                          }} 
                        />
                      ))}
                      {active.tags.length > 3 && (
                        <Tooltip title={active.tags.slice(3).join(', ')}>
                          <Chip label={`+${active.tags.length - 3}`} size="small"
                            sx={{
                              height: 18,
                              fontSize: 9.5,
                              fontWeight: 800,
                              fontFamily: 'Inter, sans-serif',
                              bgcolor: alpha(theme.palette.text.secondary, 0.1),
                              color: 'text.secondary',
                              borderRadius: 0.75,
                              cursor: 'pointer',
                            }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Box>
            <Stack direction="row" spacing={{ xs: 0.25, sm: 0.75 }} alignItems="center" sx={{ flexShrink: 0 }}>
              <Tooltip title="Contact info & tags">
                <IconButton size="small" aria-label="Contact info and tags" onClick={() => setProfileOpen(true)} 
                  sx={{ 
                    color: 'text.secondary', 
                    p: { xs: 0.5, sm: 0.75 },
                    width: { xs: 34, sm: 36 },
                    height: { xs: 34, sm: 36 },
                    '&:hover': { bgcolor: alpha(theme.palette.action.active, 0.08) },
                    transition: 'all 0.2s'
                  }}>
                  <MoreVertical size={17} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Messages */}
          <Box
            ref={scrollRef}
            onScroll={(e) => { if (e.currentTarget.scrollTop < 80) loadOlderMessages(active.id); }}
            sx={{ 
              flex: 1, 
              overflowY: 'auto', 
              py: 2, 
              minHeight: 0,
              bgcolor: theme.palette.mode === 'dark' ? '#0b141a' : '#efeae2',
              backgroundImage: theme.palette.mode === 'dark'
                ? `radial-gradient(circle, rgba(255,255,255,0.035) 1px, transparent 1px), radial-gradient(circle, rgba(255,255,255,0.02) 1px, transparent 1px)`
                : `radial-gradient(circle, rgba(0,0,0,0.04) 1px, transparent 1px)`,
              backgroundSize: '24px 24px, 12px 12px',
              backgroundPosition: '0 0, 6px 6px',
            }}
          >
            {threadLoading && !(active.messages || []).length ? (
              <Stack spacing={2} sx={{ p: 2, height: '100%', overflow: 'hidden' }}>
                {Array.from({ length: 5 }).map((_, i) => {
                  const isOut = i % 2 === 0;
                  return (
                    <Box key={i} sx={{ display: 'flex', justifyContent: isOut ? 'flex-end' : 'flex-start', width: '100%' }}>
                      <Box sx={{ display: 'flex', gap: 1, maxWidth: '70%', alignItems: 'flex-end', flexDirection: isOut ? 'row-reverse' : 'row' }}>
                        {!isOut && <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />}
                        <Box sx={{ 
                          p: 1.5, 
                          borderRadius: '16px',
                          borderTopLeftRadius: !isOut ? '2px' : '16px',
                          borderTopRightRadius: isOut ? '2px' : '16px',
                          bgcolor: isOut 
                            ? (theme.palette.mode === 'dark' ? alpha(WA_TEAL, 0.15) : alpha(WA_TEAL, 0.08))
                            : (theme.palette.mode === 'dark' ? '#202c33' : '#ffffff'),
                          border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.3) : alpha(theme.palette.divider, 0.65)}`,
                          minWidth: { xs: 120, sm: 200 },
                        }}>
                          <Skeleton variant="text" width="90%" height={12} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width={isOut ? "60%" : "80%"} height={12} sx={{ mb: 0.5 }} />
                          {i === 2 && <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: 1, my: 1 }} />}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                            <Skeleton variant="text" width={30} height={10} />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              (active.messages || []).map((m, i, arr) => {
                const prevMsg = i > 0 ? arr[i - 1] : null;
                const dayKey = (d) => { const dt = new Date(d); return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }); };
                const showDivider = !prevMsg || dayKey(prevMsg.at) !== dayKey(m.at);
                return (
                  <React.Fragment key={m.id}>
                    {showDivider && m.at && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
                        <Chip label={fmtDay(m.at)} size="small"
                          sx={{ 
                            height: 24, 
                            fontSize: 11, 
                            fontWeight: 700, 
                            fontFamily: 'Inter, sans-serif',
                            bgcolor: theme.palette.mode === 'dark' ? '#182229' : '#ffffff',
                            color: theme.palette.mode === 'dark' ? '#8696a0' : '#54656f',
                            boxShadow: theme.palette.mode === 'dark' ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(11,20,26,0.1)',
                            border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#0b141a', 0.08)}`,
                            borderRadius: 2
                          }} 
                        />
                      </Box>
                    )}
                    <MessageBubble msg={m} onEdit={startEdit} onDelete={requestDelete} onRetry={retryMessage} />
                  </React.Fragment>
                );
              })
            )}
          </Box>




          <ChatComposer
            ref={composerRef}
            active={active}
            composerActive={composerActive}
            windowOpen={windowOpen}
            editingId={editingId}
            mobile={mobile}
            fullScreen={fullScreen}
            quickRepliesList={quickRepliesList}
            initialDraft={initialDraftText}
            onConsumeInitialDraft={() => setInitialDraftText('')}
            onSendMessage={sendMessage}
            onCancelEdit={cancelEdit}
            onPickImageClick={() => imgInputRef.current?.click()}
            onPickFileClick={() => fileInputRef.current?.click()}
            onOpenTemplateDialog={() => setTemplateDialogOpen(true)}
            aiGenerating={aiGenerating}
            onAiSuggestReply={handleAiSuggestReply}
            onAiChatSummary={handleAiChatSummary}
          />

          {/* Hidden file inputs */}
          <input ref={imgInputRef} type="file" accept="image/*,video/*" hidden onChange={onPickImage} />
          <input ref={fileInputRef} type="file" hidden onChange={onPickFile} />


          {/* AI Chat Summary Dialog */}
          <Dialog open={!!aiSummary} onClose={() => setAiSummary(null)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Zap size={18} color="#6366F1" />
                <Typography sx={{ fontWeight: 800 }}>Gemini 1.5 Flash Chat Summary</Typography>
              </Box>
              <IconButton size="small" onClick={() => setAiSummary(null)}><X size={18} /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ p: 1, whiteSpace: 'pre-line', fontSize: 13.5, lineHeight: 1.6, color: 'text.primary' }}>
                {aiSummary}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 1.5 }}>
              <Button variant="contained" onClick={() => setAiSummary(null)}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      height: fullScreen ? (vpHeight ? `${vpHeight}px` : '100dvh') : { xs: 'calc(100dvh - 165px)', sm: 'calc(100dvh - 200px)', md: 'calc(100dvh - 240px)' },
      minHeight: fullScreen ? 'none' : { xs: 350, sm: 400 },
      border: fullScreen ? 'none' : `1px solid ${theme.palette.divider}`, 
      borderRadius: fullScreen ? 0 : { xs: 2, sm: 3 }, 
      overflow: 'hidden', 
      bgcolor: theme.palette.background.paper,
      touchAction: 'manipulation',
      WebkitTextSizeAdjust: '100%'
    }}>
      {listPane}
      {chatPane}
      <ProfileDrawer open={profileOpen} onClose={() => setProfileOpen(false)} conv={active}
        onAddTag={addTagToActive} onRemoveTag={removeTagFromActive} allTags={tagsList} onSetStage={changeContactStage}
        onSendCallSchedule={sendCallSchedule} />

      {/* New Chat Dialog */}
      <Dialog open={newChatDialogOpen} onClose={() => setNewChatDialogOpen(false)} {...waDialogProps(mobile, 'xs')}>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          New Chat
          <IconButton size="small" onClick={() => setNewChatDialogOpen(false)}><X size={18} /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <FormControl size="small" fullWidth>
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>Select Existing Contact</Typography>
              <Select
                value={newChatContactId}
                onChange={(e) => {
                  setNewChatContactId(e.target.value);
                  if (e.target.value) {
                    setNewChatPhone('');
                    setNewChatName('');
                  }
                }}
                displayEmpty
              >
                <MenuItem value="">-- Choose from Contacts --</MenuItem>
                {contactsList.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name} ({c.phone})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider>
              <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 800, textTransform: 'uppercase' }}>Or enter new number</Typography>
            </Divider>

            <Stack spacing={2}>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>Phone Number</Typography>
                <TextField
                  placeholder="e.g. 919409343754"
                  size="small"
                  fullWidth
                  value={newChatPhone}
                  disabled={Boolean(newChatContactId)}
                  onChange={(e) => setNewChatPhone(e.target.value)}
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>Name (Optional)</Typography>
                <TextField
                  placeholder="e.g. John Doe"
                  size="small"
                  fullWidth
                  value={newChatName}
                  disabled={Boolean(newChatContactId)}
                  onChange={(e) => setNewChatName(e.target.value)}
                />
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={waDialogActionsSx}>
          <Button onClick={() => setNewChatDialogOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            disabled={!newChatContactId && !normalizePhone(newChatPhone)}
            onClick={handleCreateNewChat}
          >
            Start Chat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Template Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} {...waDialogProps(mobile, 'xs')}>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Send Template
          <IconButton size="small" onClick={() => setTemplateDialogOpen(false)}><X size={18} /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl size="small" fullWidth>
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>Choose Template</Typography>
              <Select
                value={selectedTemplateName}
                onChange={(e) => setSelectedTemplateName(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">-- Select a Template --</MenuItem>
                {templatesList.map((t) => (
                  <MenuItem key={t.id || t.name} value={t.name}>
                    {t.name} ({t.category})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedTemplateName && templatesList.find(t => t.name === selectedTemplateName) && (
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: theme.palette.background.neutral, border: `1px solid ${theme.palette.divider}`, mt: 1 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', mb: 0.5 }}>Template Preview</Typography>
                <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', color: 'text.primary', lineHeight: 1.4 }}>
                  {templatesList.find(t => t.name === selectedTemplateName).body}
                </Typography>
                {templatesList.find(t => t.name === selectedTemplateName).footer && (
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>
                    {templatesList.find(t => t.name === selectedTemplateName).footer}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={waDialogActionsSx}>
          <Button onClick={() => setTemplateDialogOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            disabled={!selectedTemplateName}
            onClick={handleSendTemplate}
          >
            Send Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} {...waDialogProps(mobile, 'xs')}>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete this message?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            This removes the message from your inbox view. It can’t be undone, and it does not unsend a message already delivered on WhatsApp.
          </Typography>
        </DialogContent>
        <DialogActions sx={waDialogActionsSx}>
          <Button onClick={() => setDeleteTarget(null)} color="inherit">Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} startIcon={<Trash2 size={15} />}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast(t => ({ ...t, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppInbox;
